import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { hiddenByRules, legalityNote, ruleFor, useRules } from '../utils/legality';
import { Search, Filter, Layers, Compass, Star, Library, Download, Settings, MessageCircle, X, Plus, Shapes, LayoutGrid, Shirt, Archive, Network, ChevronLeft, ChevronRight, Waypoints, Swords, Rows3, Grid2x2, CircleHelp, TriangleAlert } from "lucide-react";
import type { IconComponent } from '../data/icons';
import { lingoFor, matchesLingo } from '../data/lingo';
import { useAuth } from '../hooks/useAuth';
import SettingsModal from './SettingsModal';
import { useSubscription } from '../hooks/useSubscription';
import ChatWidget from './ChatWidget';
import AdminChat from './AdminChat';
import { doc, collection as firestoreCollection, addDoc, onSnapshot as firestoreOnSnapshot, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { PipWidget, isMobile, type PipEntry } from './PipPlayer';
import { onToast } from '../utils/toast';
import FeedPlayer, { preloadFirstDiscover } from './FeedPlayer';
import BottomNav, { type TabId } from './BottomNav';
import Journey from './Journey';
import Ontology from './Ontology';
import Home from './Home';
import ViewHeader from './ViewHeader';
import { warmUserNames } from '../api/users';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import TechniqueCard from './TechniqueCard';
import TechniqueTile from './TechniqueTile';
import VersionPicker from './VersionPicker';
import CategoryCard from './CategoryCard';
import CollectionChips from './CollectionChips';
import TutorialOverlay, { type TutorialStep } from './Tutorial';
import AddTechniqueModal from './AddTechniqueModal';
import TechniqueInfoModal from './TechniqueInfoModal';
import { categories, categoryInArts } from '../data/categories';
import { CATEGORY_ICONS, TYPE_ICONS } from '../data/icons';
import { useTechniqueStatus } from '../hooks/useTechniqueStatus';
import { useTechniques } from '../hooks/useTechniques';
import { getThumbnailUrl } from '../utils/video';
import { showToast } from '../utils/toast';
import { groupTechniques, type TechniqueGroup } from '../utils/groupTechniques';
import { useAdminQueue } from '../hooks/useAdminQueue';
import { Art, BeltRank, TechniqueStatus, TechniqueType, Uniform } from '../types';
import { TIER_ACCENT_COLORS, TYPE_ORDER } from '../constants/layout';
import { ARTS, ART_META, TIERS, getTierLabelsFor, showUniformFilterFor, sortArts, techInArts, tierRungs } from '../data/arts';
import ArtPicker from './ArtPicker';
import PreviewWall from './PreviewWall';
import { previewExhausted, recordPreviewPlay } from '../previewGate';
import { onRequestAccount } from '../guestMode';
import ArtEmptyState from './ArtEmptyState';
import type { Technique, Collection } from '../types';
import { loadCustomTechniques, saveCustomTechniques, loadCollections, saveCollections, setStorageUser, userKey } from '../utils/storage';

// Developer chat is switched off for now — the forum covers the same need.
// Only the entry points are gated: ChatWidget, the 'help' tab, and the unread
// tracking are all left wired up, so flipping this back to true restores it.
const DEV_CHAT_ENABLED: boolean = false;

function useAccordion() {
  const [openSet, setOpenSet] = useState<Set<string>>(new Set());

  const toggle = useCallback((id: string) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const open = useCallback((id: string) => {
    setOpenSet((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const close = useCallback((id: string) => {
    setOpenSet((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const closeAll = useCallback(() => {
    setOpenSet(new Set());
  }, []);

  const isOpen = useCallback((id: string) => openSet.has(id), [openSet]);

  return { toggle, open, close, closeAll, isOpen };
}

// Uniform filter chips.
const UNIFORM_OPTIONS: { value: Uniform | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: Uniform.Gi, label: 'Gi' },
  { value: Uniform.NoGi, label: 'No-Gi' },
];

// Rank filter chips, in display order (combined view first, then highest rung
// down). Labels are art-specific (BJJ belts vs generic levels), and built from
// the rungs rather than the five tiers — the neutral scale merges its bottom
// two into Beginner, and a chip per tier would show that name twice.
function rankOptions(arts: Art[]): { value: BeltRank | 'all'; label: string; tiers: BeltRank[] }[] {
  const rungs = tierRungs(getTierLabelsFor(arts));
  return [
    { value: 'all', label: 'All', tiers: [] },
    ...rungs.slice().reverse().map((r) => ({ value: r.tier, label: r.label, tiers: r.tiers })),
  ];
}

// Level selections are stored as a set of rung tiers (empty = every level).
// They used to be a single choice persisted as "3" / "all", so an old value
// still loads, as a one-entry selection.
function parseRankFilter(saved: string | null): Set<BeltRank> {
  if (!saved || saved === 'all') return new Set();
  try {
    const parsed = JSON.parse(saved);
    if (Array.isArray(parsed)) return new Set(parsed.map(Number) as BeltRank[]);
  } catch { /* fall through to the old single-value format */ }
  const n = Number(saved);
  return Number.isNaN(n) ? new Set() : new Set([n as BeltRank]);
}

// Column count for a group's PiP grid, chosen so the tiles form a compact
// rectangle (2x2, 3x2, 4x2…) instead of stretching out into a single row.
function pipGridCols(n: number): number {
  if (n <= 2) return n;   // 1 or 2 across
  if (n <= 4) return 2;   // 2x2
  if (n <= 6) return 3;   // 3x2
  return 4;               // 4x2 (up to PIP_PAGE_SIZE = 8)
}

type GroupPlacement = { x: number; y: number; scale: number };

// Spawn a group in the empty gutter beside the desktop phone frame — to the right
// if the tiles fit there at full size, else the left, else whichever gutter is
// wider. The group's content is centred in the viewport, so we translate it into
// the chosen gutter's centre and scale it down to fit the gutter width + viewport
// height (a full page of portrait tiles is taller/wider than a laptop gutter).
function groupPlacement(tileCount: number): GroupPlacement {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cols = pipGridCols(tileCount);
  const rows = Math.ceil(tileCount / cols);
  const tileW = Math.max(120, Math.min(0.15 * vw, 220)); // mirror the CSS clamp(120px, 15vw, 220px)
  const tileH = (tileW * 16) / 9;                        // tiles are 9:16 portrait
  const naturalW = cols * tileW + (cols - 1) * 8;        // + column gaps
  const naturalH = 48 + rows * tileH + (rows - 1) * 8;   // + bar and row gaps
  const availH = vh - 24;

  const frame = document.querySelector('.phone-frame')?.getBoundingClientRect();
  if (!frame) return { x: 0, y: 0, scale: Math.min(1, availH / naturalH) };

  const gap = 24; // breathing room between the group and the frame/edge
  const gutterR = Math.max(0, vw - frame.right - gap);
  const gutterL = Math.max(0, frame.left - gap);
  const side = gutterR >= naturalW || gutterR >= gutterL ? 'right' : 'left';
  const gutterW = side === 'right' ? gutterR : gutterL;
  // Centre of the chosen gutter, in viewport coords.
  const gutterCenter = side === 'right' ? (frame.right + vw) / 2 : frame.left / 2;
  const scale = Math.min(1, gutterW / naturalW, availH / naturalH);
  // Content is centred at vw/2; translate is applied after scale (unscaled px).
  return { x: gutterCenter - vw / 2, y: 0, scale };
}

// Bar atop a group's PiP grid: shows the group title, pages through the videos,
// and closes them all. Dragging the bar (anywhere but a button) moves the whole
// group — it reports a running offset that the parent applies to the container.
function PipGroupBar({ title, page, pages, onPrev, onNext, onClose, offset, onOffsetChange }: {
  title?: string;
  page: number;
  pages: number;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  offset: GroupPlacement;
  onOffsetChange: (offset: GroupPlacement) => void;
}) {
  const drag = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const onPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { startX: e.clientX, startY: e.clientY, origX: offset.x, origY: offset.y };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    onOffsetChange({
      ...offset, // keep scale
      x: drag.current.origX + (e.clientX - drag.current.startX),
      y: drag.current.origY + (e.clientY - drag.current.startY),
    });
  };
  const onPointerUp = (e: React.PointerEvent) => {
    drag.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }
  };
  return (
    <div
      className="pip-group-bar"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {title && <span className="pip-group-title">{title}</span>}
      {pages > 1 && (
        <span className="pip-group-pager">
          <button className="pip-group-page-btn" onClick={onPrev} aria-label="Previous videos"><ChevronLeft size={14} /></button>
          <span className="pip-group-page-num">{page + 1}/{pages}</span>
          <button className="pip-group-page-btn" onClick={onNext} aria-label="More videos"><ChevronRight size={14} /></button>
        </span>
      )}
      <button className="pip-group-close-btn" onClick={onClose} aria-label="Close all videos" title="Close all videos">
        <X size={14} />
      </button>
    </div>
  );
}

export default function AppShell({ preview = false, guest = false, onDemoCursor, arts = [Art.Bjj], onChangeArts, initialTechniqueId }: {
  preview?: boolean;
  /** Free preview: signed out, library index from the public endpoint, ten
   *  plays before the wall. Nothing here writes to Firestore. */
  guest?: boolean;
  onDemoCursor?: (cursor: { x: number; y: number; visible: boolean; key?: number }) => void;
  /** The grappling arts the whole app is scoped to (chosen in the ArtPicker gate). */
  arts?: Art[];
  onChangeArts?: (arts: Art[]) => void;
  /** Arrived on a /t/<id> share link — open that technique once the pool loads. */
  initialTechniqueId?: string;
}) {
  const appRef = useRef<HTMLDivElement>(null);
  const { user, logOut } = useAuth();
  const sub = useSubscription(user);
  const { statusMap, setStatus, cycleStatus } = useTechniqueStatus(preview || guest ? null : user?.uid ?? null);
  const { techniques: dbTechniques, loading: dbLoading } = useTechniques({ guest });
  const [customTechniques, setCustomTechniques] = useState<Technique[]>(loadCustomTechniques);
  const [addModalCat, setAddModalCat] = useState<{ id: string; label: string; tier: BeltRank } | null>(null);
  // Initialize storage user context before accessing filters
  if (!preview && !guest) setStorageUser(user?.uid ?? null);

  const [searchQuery, setSearchQuery] = useState('');
  /** The other name a search found this technique by — set only when the
   *  query matched the Japanese name and not the name itself, so the row can
   *  say why it came up ("matches Juji Gatame"). */
  const aliasMatch = (t: { name: string; japaneseName?: string; aliases?: string[] }): string | undefined => {
    const q = searchQuery.trim().toLowerCase();
    if (!q || t.name.toLowerCase().includes(q)) return undefined;
    return [t.japaneseName, ...(t.aliases ?? [])].find((a) => a?.toLowerCase().includes(q));
  };
  const catAccordion = useAccordion();

  // What a category row says under its name, and the clips it peeks at while
  // closed. Counts what the row would show: gi/no-gi pairs are one entry,
  // and a creator is counted once however many clips they filmed.
  const categoryRowProps = useCallback((groups: TechniqueGroup[]) => {
    const n = groups.length;
    const creators = new Set(groups.flatMap((g) => g.variants.map((t) => t.author).filter(Boolean))).size;
    const meta = `${n} technique${n === 1 ? '' : 's'}${creators ? ` · ${creators} creator${creators === 1 ? '' : 's'}` : ''}`;
    // One thumb per group, from whichever clip in it has one; groups with
    // none are skipped rather than leaving a gap in the strip.
    const peek = groups
      .map((g) => g.variants.map((t) => getThumbnailUrl(t.instagramUrl, t.thumbnail)).find(Boolean))
      .filter((src): src is string => !!src)
      .filter((src, i, all) => all.indexOf(src) === i)
      .slice(0, 3);
    return { meta, peek };
  }, []);

  // Filters are loaded from localStorage once user is available.
  // We track the uid they were loaded for and re-read if it changes.
  const filtersUid = useRef<string | null>(null);
  const [rankFilter, setRankFilter] = useState<Set<BeltRank>>(new Set());
  const [uniformFilter, setUniformFilter] = useState<Uniform | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<TechniqueStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
  // Sport rules: banned-in-this-art techniques are hidden unless asked for.
  const [showBanned, setShowBanned] = useState(false);
  const rules = useRules();

  // Load per-user filter state from localStorage whenever the signed-in uid
  // changes. setState-in-effect is intentional here — filters are per-user and
  // must reset on sign-in/sign-out.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (preview || !user || filtersUid.current === user.uid) return;
    filtersUid.current = user.uid;
    setRankFilter(parseRankFilter(localStorage.getItem(userKey('gnosis-rank-filter'))));
    const savedUniform = localStorage.getItem(userKey('gnosis-uniform-filter'));
    setUniformFilter(savedUniform === 'gi' || savedUniform === 'nogi' ? savedUniform : 'all');
    try {
      const catSaved = localStorage.getItem(userKey('gnosis-category-filter'));
      if (catSaved) setCategoryFilter(new Set(JSON.parse(catSaved) as string[]));
    } catch { /* ignore */ }
    try {
      const typeSaved = localStorage.getItem(userKey('gnosis-type-filter'));
      if (typeSaved) setTypeFilter(new Set(JSON.parse(typeSaved) as string[]));
    } catch { /* ignore */ }
    setShowBanned(localStorage.getItem(userKey('gnosis-show-banned')) === '1');
  }, [preview, user]);
  /* eslint-enable react-hooks/set-state-in-effect */
  const [showFilter, setShowFilter] = useState(false);
  // Which filter sections to show in the panel ('all' = full panel). Set by the
  // Library start screen so the user can begin with just one dimension.
  const [filterScope, setFilterScope] = useState<'all' | 'position' | 'type' | 'level'>('all');
  const [showFeed, setShowFeed] = useState(false);
  // Screenshot-capture hook: `?preview&capture&view=<tab>` opens a specific tab
  // with the demo loop + splash off, so the landing-page screenshot script can
  // grab each view deterministically. No effect outside preview.
  const captureParams = preview ? new URLSearchParams(window.location.search) : null;
  const captureMode = !!captureParams?.has('capture');
  const captureView = (captureParams?.get('view') || null) as TabId | null;

  // Primary navigation. The app always opens on Home on a fresh load,
  // regardless of the last-used tab.
  const [activeTab, setActiveTab] = useState<TabId>(
    captureMode && captureView ? captureView : 'home'
  );
  /** Which wall a guest has run into, if any. */
  const [wallReason, setWallReason] = useState<null | 'plays' | 'locked'>(null);
  // Controls that live in the shell but need an account behind them ask for
  // the wall by event, rather than by a callback threaded through every view.
  useEffect(() => onRequestAccount((reason) => setWallReason(reason)), []);
  // Favorites is its own footer tab now: the Library list filtered to the
  // user's starred (practicing) / completed techniques. Derived from the tab so
  // there's a single source of truth — no separate toggle state to keep in sync.
  const myMatOnly = activeTab === 'favorites';
  // Within the Favorites tab, an "Archived" sub-view shows the techniques the
  // user has archived (done with), each with an un-archive action.
  const [archivedView, setArchivedView] = useState(false);
  // User-made collections of favorites (per-device). activeCollectionId filters
  // the Favorites list to one collection; null = all favorites.
  const [collections, setCollections] = useState<Collection[]>(loadCollections);
  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(null);
  const mutateCollections = useCallback((fn: (prev: Collection[]) => Collection[]) => {
    setCollections((prev) => { const next = fn(prev); saveCollections(next); return next; });
  }, []);
  const createCollection = useCallback((name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    mutateCollections((prev) => [...prev, { id: `c-${Date.now()}`, name: trimmed, techniqueIds: [] }]);
  }, [mutateCollections]);
  const deleteCollection = useCallback((id: string) => {
    mutateCollections((prev) => prev.filter((c) => c.id !== id));
    setActiveCollectionId((prev) => (prev === id ? null : prev));
  }, [mutateCollections]);
  const addToCollection = useCallback((collectionId: string, techId: string) => {
    mutateCollections((prev) => prev.map((c) =>
      c.id === collectionId && !c.techniqueIds.includes(techId)
        ? { ...c, techniqueIds: [...c.techniqueIds, techId] } : c));
  }, [mutateCollections]);
  const removeFromCollection = useCallback((collectionId: string, techId: string) => {
    mutateCollections((prev) => prev.map((c) =>
      c.id === collectionId ? { ...c, techniqueIds: c.techniqueIds.filter((i) => i !== techId) } : c));
  }, [mutateCollections]);
  const [soloVideo, setSoloVideo] = useState<{ url: string; name: string; subtitle?: string; techniqueId?: string } | null>(null);
  // Technique whose details modal is open (info icon on a card).
  const [infoTech, setInfoTech] = useState<Technique | null>(null);
  // The technique group whose version chooser is open. Set by tapping a
  // collapsed card/tile; cleared when a version is picked or the sheet is
  // dismissed. Only ever one at a time.
  const [versionGroup, setVersionGroup] = useState<TechniqueGroup | null>(null);
  const [unreadChat, setUnreadChat] = useState(0);

  // Admin queue: pending submissions (green) + admin-archived items (yellow).
  // Empty for non-admins. Non-admin visibility is enforced by Firestore rules.
  const isAdminUser = user?.uid === 'bZj8y9DApEac3eKOuKV08MKcaDx2';
  // Janitors share the admin's pencil + archive on a technique (enforced
  // server-side too); everything else admin-only stays admin-only.
  const canEditLibrary = isAdminUser || sub.janitor;
  const { pending: pendingSubs, archived: archivedTechs } = useAdminQueue(!preview && isAdminUser);
  // Submissions handled this session. The snapshot normally removes handled
  // rows, but if the listener has gone stale a row can linger and re-approving
  // it fails with "Already processed" — so track handled ids locally too.
  const [processedSubIds, setProcessedSubIds] = useState<Set<string>>(new Set());
  const markSubProcessed = useCallback((id: string) => {
    setProcessedSubIds((prev) => { const next = new Set(prev); next.add(id); return next; });
  }, []);
  const pendingByCat = useMemo(() => {
    const m = new Map<string, Technique[]>();
    for (const p of pendingSubs) {
      if (processedSubIds.has(p.id)) continue;
      const list = m.get(p.categoryId) || [];
      list.push(p);
      m.set(p.categoryId, list);
    }
    return m;
  }, [pendingSubs, processedSubIds]);
  const archivedByCat = useMemo(() => {
    const m = new Map<string, Technique[]>();
    for (const a of archivedTechs) {
      const list = m.get(a.categoryId) || [];
      list.push(a);
      m.set(a.categoryId, list);
    }
    return m;
  }, [archivedTechs]);

  // Admin-only library mode: show ONLY pending submissions or ONLY archived
  // techniques in the library (entered from Settings → View Pending/Archived
  // Media; tapping any bottom-nav tab resets back to the normal library).
  const [adminViewMode, setAdminViewMode] = useState<'pending' | 'archived' | null>(null);

  const runCallable = useCallback((name: string, payload: Record<string, unknown>) => {
    return httpsCallable(functions, name)(payload).catch((e) => { console.error(`${name} failed:`, e); });
  }, []);
  const resolveSubmission = useCallback((name: string, t: Technique, payload: Record<string, unknown>) => {
    return httpsCallable(functions, name)(payload).then(
      () => markSubProcessed(t.id),
      (e) => {
        // failed-precondition = "Already processed": approved/rejected in an
        // earlier call or another session — either way the row is done.
        if ((e as { code?: string })?.code === 'functions/failed-precondition') markSubProcessed(t.id);
        else console.error(`${name} failed:`, e);
      },
    );
  }, [markSubProcessed]);
  const approvePending = useCallback((t: Technique) => resolveSubmission('approveSubmission', t, {
    submissionId: t.id, name: t.name, categoryId: t.categoryId, tier: t.tier, type: t.type,
  }), [resolveSubmission]);
  const rejectPending = useCallback((t: Technique) => resolveSubmission('rejectSubmission', t, { submissionId: t.id }), [resolveSubmission]);
  const restoreArchived = useCallback((t: Technique) => runCallable('restoreTechnique', { techniqueId: t.id }), [runCallable]);
  const archiveTechnique = useCallback((t: Technique) => runCallable('archiveTechnique', { techniqueId: t.id }), [runCallable]);
  const deleteArchived = useCallback((t: Technique) => {
    if (!confirm(`Permanently delete "${t.name}"?`)) return Promise.resolve();
    return runCallable('deleteTechnique', { techniqueId: t.id });
  }, [runCallable]);

  const [exporting, setExporting] = useState(false);
  const exportLibrary = useCallback(async () => {
    setExporting(true);
    try {
      const result = await httpsCallable(functions, 'exportTechniques')({});
      const json = JSON.stringify(result.data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `gnosis-techniques-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
    } finally {
      setExporting(false);
    }
  }, []);

  // Listen for unread chat messages
  useEffect(() => {
    if (preview || !user) return;
    const isAdmin = user.uid === 'bZj8y9DApEac3eKOuKV08MKcaDx2';
    if (isAdmin) {
      return firestoreOnSnapshot(firestoreCollection(db, 'chats'),
        (snap) => {
          const total = snap.docs.reduce((sum, d) => sum + (d.data().unreadAdmin || 0), 0);
          setUnreadChat(total);
          // Pre-warm the resolved-name cache so the chat page shows real names
          // immediately instead of flickering UID → name on first open.
          warmUserNames(snap.docs.map((d) => d.id).filter((id) => id !== 'bZj8y9DApEac3eKOuKV08MKcaDx2'));
        },
        () => { /* ignore */ }
      );
    } else {
      return firestoreOnSnapshot(doc(db, 'chats', user.uid),
        (snap) => {
          setUnreadChat(snap.exists() ? (snap.data()?.unreadUser || 0) : 0);
        },
        () => { /* ignore */ }
      );
    }
  }, [preview, user]);

  // Persist filter selections (only after filters have been loaded from localStorage)
  useEffect(() => {
    if (preview || !filtersUid.current) return;
    localStorage.setItem(userKey('gnosis-category-filter'), JSON.stringify([...categoryFilter]));
    localStorage.setItem(userKey('gnosis-type-filter'), JSON.stringify([...typeFilter]));
    localStorage.setItem(userKey('gnosis-rank-filter'), JSON.stringify([...rankFilter]));
    localStorage.setItem(userKey('gnosis-uniform-filter'), String(uniformFilter));
    localStorage.setItem(userKey('gnosis-show-banned'), showBanned ? '1' : '0');
  }, [preview, statusFilter, categoryFilter, typeFilter, rankFilter, uniformFilter, showBanned]);

  // Help & Settings render as in-frame pages. Remember the content tab we came
  // from so closing them (the X) returns there instead of a fixed default.
  const returnTabRef = useRef<TabId>('library');
  useEffect(() => {
    if (activeTab !== 'help' && activeTab !== 'settings') returnTabRef.current = activeTab;
  }, [activeTab]);
  const closeOverlayTab = useCallback(() => setActiveTab(returnTabRef.current), []);

  // Click-and-drag horizontal scroll on filter bars
  const dragState = useRef<{ el: HTMLElement | null; startX: number; scrollLeft: number; dragged: boolean }>({ el: null, startX: 0, scrollLeft: 0, dragged: false });
  useEffect(() => {
    const ds = dragState.current;

    const onDown = (e: MouseEvent) => {
      const bar = (e.target as HTMLElement).closest('.category-filter-bar') as HTMLElement | null;
      if (!bar) return;
      ds.el = bar;
      ds.startX = e.pageX;
      ds.scrollLeft = bar.scrollLeft;
      ds.dragged = false;
      bar.style.cursor = 'grabbing';
      bar.style.userSelect = 'none';
    };

    const onMove = (e: MouseEvent) => {
      if (!ds.el) return;
      const dx = e.pageX - ds.startX;
      if (Math.abs(dx) > 3) ds.dragged = true;
      if (ds.dragged) {
        ds.el.scrollLeft = ds.scrollLeft - dx;
      }
    };

    const onUp = () => {
      if (!ds.el) return;
      ds.el.style.cursor = 'grab';
      ds.el.style.userSelect = '';
      ds.el = null;
    };

    const onClick = (e: MouseEvent) => {
      if (ds.dragged && (e.target as HTMLElement).closest('.category-filter-bar')) {
        e.stopPropagation();
        e.preventDefault();
      }
      ds.dragged = false;
    };

    document.addEventListener('mousedown', onDown);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('click', onClick, true);
    };
  }, []);

  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  // Which PiP window to bounce (clip already open) + a nonce to replay it.
  const [pipBounce, setPipBounce] = useState<{ id: string; n: number }>({ id: '', n: 0 });
  const [pipWindows, setPipWindows] = useState<PipEntry[]>(() => {
    if (preview) return [];
    try {
      const saved = localStorage.getItem(userKey('gnosis-pip-windows'));
      if (!saved) return [];
      const parsed: PipEntry[] = JSON.parse(saved);
      return parsed.map((w) => ({ ...w, dormant: true }));
    } catch { return []; }
  });
  const savePipWindows = useCallback((windows: PipEntry[]) => {
    localStorage.setItem(userKey('gnosis-pip-windows'), JSON.stringify(windows));
  }, []);
  // Always-current copy of the open windows so addPipWindow can dedupe by url.
  const pipWindowsRef = useRef(pipWindows);
  useEffect(() => { pipWindowsRef.current = pipWindows; }, [pipWindows]);
  // Find a tiled, non-overlapping slot for a new desktop PiP window. Windows
  // tile left-to-right in the space right of the app panel; overflow rows
  // cascade diagonally so no two windows open stacked on the same spot.
  const computePipPosition = useCallback((existing: PipEntry[]) => {
    const rect = appRef.current?.getBoundingClientRect();
    const baseX = rect ? rect.right + 16 : 40;
    const baseY = rect ? Math.max(rect.top, 8) : 40;
    const STEP_X = 236; // portrait widget width (~220) + gap
    const STEP_Y = 96;  // diagonal offset for overflow rows
    const occupied = (x: number, y: number) =>
      existing.some((w) => w.x != null && w.y != null && Math.abs(w.x - x) < 20 && Math.abs(w.y - y) < 20);
    const maxCols = Math.max(1, Math.floor((window.innerWidth - baseX) / STEP_X));
    for (let i = 0; i < 64; i++) {
      const col = i % maxCols;
      const row = Math.floor(i / maxCols);
      const x = baseX + col * STEP_X + row * STEP_Y;
      const y = baseY + row * STEP_Y;
      if (!occupied(x, y)) return { x, y };
    }
    return { x: baseX, y: baseY };
  }, []);
  // The width the last PiP was resized to: a new window opens at that size
  // rather than the built-in default, so one resize sets the size for the
  // session and beyond.
  const lastPipWidth = useRef<number | null>((() => {
    try { const v = Number(localStorage.getItem('gnosis-pip-width')); return v > 0 ? v : null; } catch { return null; }
  })());
  const addPipWindow = useCallback((entry: PipEntry) => {
    // Don't open the same clip twice (click or drag-out) — bounce the existing.
    const existing = pipWindowsRef.current.find((w) => w.url === entry.url);
    if (existing) { setPipBounce({ id: existing.id, n: Date.now() }); return; }
    setPipWindows((prev) => {
      const sized = entry.w == null && lastPipWidth.current ? { ...entry, w: lastPipWidth.current } : entry;
      const placed = (sized.x == null || sized.y == null)
        ? { ...sized, ...computePipPosition(prev) }
        : sized;
      const next = [...prev, placed];
      savePipWindows(next);
      return next;
    });
  }, [savePipWindows, computePipPosition]);
  const closePipWindow = useCallback((id: string) => {
    setPipWindows((prev) => { const next = prev.filter((w) => w.id !== id); savePipWindows(next); return next; });
  }, [savePipWindows]);
  const updatePipPosition = useCallback((id: string, x: number, y: number) => {
    setPipWindows((prev) => {
      const next = prev.map((w) => w.id === id ? { ...w, x, y } : w);
      savePipWindows(next);
      return next;
    });
  }, [savePipWindows]);
  const updatePipSize = useCallback((id: string, w: number, h: number) => {
    if (w > 0) {
      lastPipWidth.current = w;
      try { localStorage.setItem('gnosis-pip-width', String(Math.round(w))); } catch { /* private mode */ }
    }
    setPipWindows((prev) => {
      const next = prev.map((e) => e.id === id ? { ...e, w, h } : e);
      savePipWindows(next);
      return next;
    });
  }, [savePipWindows]);
  // Lay out `n` portrait players in a centred grid sized to the viewport, so a
  // whole collection/category opens at once without overlap.
  const PIP_PAGE_SIZE = 8;
  type PipVideo = { url: string; name: string; subtitle?: string; techniqueId?: string };
  type PipGroup = { items: PipVideo[]; page: number; title?: string };
  // Groups can hold more videos than fit on screen, so we keep the full list +
  // current page here and only render PIP_PAGE_SIZE windows at a time; the group
  // bar's prev/next swaps the page in place. The tiles flow in a responsive CSS
  // grid (see .pip-group-grid), so there's no JS layout — just which clips show.
  const [pipGroups, setPipGroups] = useState<Record<string, PipGroup>>({});
  // Drag offset per group, applied as a translate() on the whole grid container
  // so dragging the bar moves the bar + all its tiles together.
  const [pipGroupOffsets, setPipGroupOffsets] = useState<Record<string, GroupPlacement>>({});
  // Render one page of a group: replace that group's windows with the page slice,
  // all dormant (no autoplay). Positioning/sizing is handled by CSS, not here.
  const showPipGroupPage = useCallback((groupId: string, group: PipGroup) => {
    const { items, page, title } = group;
    const slice = items.slice(page * PIP_PAGE_SIZE, page * PIP_PAGE_SIZE + PIP_PAGE_SIZE);
    const windows: PipEntry[] = slice.map((it, i) => ({
      id: `${groupId}-${page * PIP_PAGE_SIZE + i}`,
      url: it.url, name: it.name, subtitle: it.subtitle, techniqueId: it.techniqueId,
      dormant: true, groupId, groupTitle: title,
    }));
    setPipWindows((prev) => {
      const next = [...prev.filter((w) => w.groupId !== groupId), ...windows];
      savePipWindows(next);
      return next;
    });
  }, [savePipWindows]);
  const pagePipGroup = useCallback((groupId: string, dir: 1 | -1) => {
    setPipGroups((prev) => {
      const g = prev[groupId];
      if (!g) return prev;
      const pages = Math.ceil(g.items.length / PIP_PAGE_SIZE);
      const ng: PipGroup = { ...g, page: (g.page + dir + pages) % pages };
      showPipGroupPage(groupId, ng);
      return { ...prev, [groupId]: ng };
    });
  }, [showPipGroupPage]);
  const closePipGroup = useCallback((groupId: string) => {
    setPipWindows((prev) => { const next = prev.filter((w) => w.groupId !== groupId); savePipWindows(next); return next; });
    setPipGroups((prev) => { const next = { ...prev }; delete next[groupId]; return next; });
    setPipGroupOffsets((prev) => { const next = { ...prev }; delete next[groupId]; return next; });
  }, [savePipWindows]);
  const loadWatched = () => {
    try {
      const saved = localStorage.getItem(userKey('gnosis-watched'));
      return saved ? new Set(JSON.parse(saved) as string[]) : new Set<string>();
    } catch { return new Set<string>(); }
  };
  const [watchedUrls, setWatchedUrls] = useState<Set<string>>(() => (preview ? new Set<string>() : loadWatched()));
  // AppShell's own useAuth starts null on a fresh load, so the per-device
  // reads above (collections, watched) happen before the uid is known and
  // hit the signed-out key. Re-read whenever the uid changes — during render,
  // the way React wants state derived from a changed prop, not in an effect.
  const [storageUid, setStorageUid] = useState<string | null>(null);
  if (!preview && storageUid !== (user?.uid ?? null)) {
    setStorageUid(user?.uid ?? null);
    setCollections(loadCollections());
    setWatchedUrls(loadWatched());
  }

  // Insertion order is play order, so the set doubles as the Recently
  // watched list: a replay moves the clip to the end (most recent).
  const markWatched = useCallback((url: string) => {
    setWatchedUrls((prev) => {
      const next = new Set(prev);
      next.delete(url);
      next.add(url);
      localStorage.setItem(userKey('gnosis-watched'), JSON.stringify([...next]));
      return next;
    });
  }, []);

  // Storage user context is set above before filter initialization

  const handlePipClick = useCallback((url: string, name: string, subtitle?: string, techniqueId?: string) => {
    // Library playback spends the same allowance the feed does, otherwise the
    // ten free clips are only a limit on one of the two ways to watch.
    if (guest) {
      if (previewExhausted()) { setWallReason('plays'); return; }
      if (techniqueId) recordPreviewPlay(techniqueId);
    }
    markWatched(url);
    if (isMobile()) {
      setSoloVideo({ url, name, subtitle, techniqueId });
    } else {
      addPipWindow({ id: `${Date.now()}`, url, name, subtitle, techniqueId });
    }
  }, [addPipWindow, markWatched, guest]);


  // Multi-select: add/remove the chip from the filter set.
  const toggleCategory = useCallback((catId: string) => {
    setCategoryFilter((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId); else next.add(catId);
      return next;
    });
  }, []);

  const toggleRank = useCallback((tier: BeltRank) => {
    setRankFilter((prev) => {
      const next = new Set(prev);
      if (next.has(tier)) next.delete(tier); else next.add(tier);
      return next;
    });
  }, []);

  const toggleType = useCallback((type: string) => {
    setTypeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type); else next.add(type);
      return next;
    });
  }, []);

  // Filter out local custom techniques whose video now exists in the public DB
  const dedupedCustom = useMemo(() => {
    if (dbTechniques.length === 0 || customTechniques.length === 0) return customTechniques;
    const publicUrls = new Set(dbTechniques.map((t) => t.instagramUrl).filter(Boolean));
    const remaining = customTechniques.filter((t) => !t.instagramUrl || !publicUrls.has(t.instagramUrl));
    if (remaining.length < customTechniques.length) {
      saveCustomTechniques(remaining);
      return remaining;
    }
    return customTechniques;
  }, [dbTechniques, customTechniques]);

  // Identity key for the selection, so the memos below don't re-run when a
  // caller passes a fresh array with the same contents.
  const artsKey = arts.join(',');

  // Scope the whole pool to the selected arts. Every view (Library, Favorites,
  // Discover, Paths, Series) derives from `techniques`, so this one filter is
  // the only place the art dimension is applied. A technique shows if it
  // belongs to *any* selected art.
  const techniques = useMemo(
    () => [...dbTechniques, ...dedupedCustom].filter((t) => techInArts(t, arts)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dbTechniques, dedupedCustom, artsKey]
  );

  // Does the selection include BJJ? The Discover/Series/Gallery catalogs are
  // BJJ-only for now, so they fall back to the coming-soon state without it.
  const hasBjj = arts.includes(Art.Bjj);

  // Sub-filters are scoped to what actually exists for the selected arts — no
  // point offering Turtle when only Judo is selected, since it isn't a Judo
  // position. Derived from the pool rather than hand-tagged on each category,
  // so content added for a new art brings its own options along with it. A
  // section with nothing left to offer is hidden entirely (see `show` below).
  const availableCategoryIds = useMemo(() => new Set(techniques.map((t) => t.categoryId)), [techniques]);
  const availableTypes = useMemo(() => new Set<string>(techniques.map((t) => t.type)), [techniques]);
  const availableRanks = useMemo(() => new Set<BeltRank>(techniques.map((t) => t.tier)), [techniques]);
  // Two gates: the position has to belong to a selected art (so a wrestler is
  // never offered De la Riva off one mis-tagged clip) AND have something in it
  // (so no chip is a dead end). Position art tags live in categories.json.
  const visibleCategories = useMemo(
    () => categories.filter((c) => availableCategoryIds.has(c.id) && categoryInArts(c, arts)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [availableCategoryIds, artsKey]
  );

  // Tier labels for the selection (BJJ belts when BJJ is the only art, generic
  // levels for anything mixed) used everywhere a tier is shown.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tierLabels = useMemo(() => getTierLabelsFor(arts), [artsKey]);
  // Only rungs that actually have content, plus the "All" entry. A rung can
  // cover two tiers, so it stays as long as either of them has clips —
  // filtering on the rung's own tier alone would drop Beginner from a library
  // whose easiest clips all sit on the other half of it.
  const RANK_OPTIONS = useMemo(
    () => rankOptions(arts).filter((o) => o.value === 'all' || o.tiers.some((t) => availableRanks.has(t))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [artsKey, availableRanks]
  );

  // The picked levels as rung labels — a rung can cover two tiers, so matching
  // by label keeps both of them in when that rung's name is picked.
  const selectedRankLabels = useMemo(
    () => new Set([...rankFilter].map((t) => tierLabels[t])),
    [rankFilter, tierLabels]
  );

  // Clear filters that no longer apply after an art change, so a chip the user
  // can no longer see isn't silently excluding everything. setState-in-effect
  // is intentional — same pattern as the per-user filter reload above.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!showUniformFilterFor(arts)) setUniformFilter('all');
    setCategoryFilter((prev) => {
      const next = new Set([...prev].filter((id) => availableCategoryIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
    setTypeFilter((prev) => {
      const next = new Set([...prev].filter((v) => availableTypes.has(v)));
      return next.size === prev.size ? prev : next;
    });
    setRankFilter((prev) => {
      const next = new Set([...prev].filter((t) => RANK_OPTIONS.some((o) => o.value !== 'all' && o.tiers.includes(t))));
      return next.size === prev.size ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artsKey]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // "All" on the art row means every art selected — the selection can't be
  // empty the way the Position/Type filters can, since the app always needs at
  // least one art (the gate enforces the same rule).
  const allArtsSelected = arts.length === ARTS.length;

  const toggleArtFilter = useCallback((a: Art) => {
    if (!onChangeArts) return;
    // Coming from "All", tapping one art narrows to just that art — the same
    // move the Position and Type rows make when you pick a chip out of All.
    if (arts.length === ARTS.length) { onChangeArts([a]); return; }
    const next = arts.includes(a) ? arts.filter((x) => x !== a) : sortArts([...arts, a]);
    if (next.length > 0) onChangeArts(next);
  }, [arts, onChangeArts]);

  // Dismissing an art pill. Clearing the last one lands back on every art —
  // "no art filter" is All here, the same place unticking the final Position
  // chip leaves you.
  const removeArtFilter = useCallback((a: Art) => {
    if (!onChangeArts) return;
    const next = arts.filter((x) => x !== a);
    onChangeArts(next.length > 0 ? next : ARTS.map((x) => x.id));
  }, [arts, onChangeArts]);

  // Library layout: rows (default) or a thumbnail grid. Per-device, and shared
  // by Library, Favorites and Archived since they render the same cards.
  const [libraryView, setLibraryView] = useState<'list' | 'grid'>(() => {
    try {
      return localStorage.getItem('gnosis-library-view') === 'grid' ? 'grid' : 'list';
    } catch {
      return 'list';
    }
  });
  const toggleLibraryView = useCallback(() => {
    setLibraryView((v) => {
      const next = v === 'grid' ? 'list' : 'grid';
      try { localStorage.setItem('gnosis-library-view', next); } catch { /* private mode */ }
      return next;
    });
  }, []);

  // Whether the Discover modal's filter panel is expanded. Starts collapsed —
  // the modal opens showing just what's currently filtered, so getting to the
  // feed stays one swipe. Kept separate from `showFilter` so opening it here
  // doesn't leave the Library's panel expanded too.
  const [discoverFilterOpen, setDiscoverFilterOpen] = useState(false);

  // Art switcher overlay (opened from Settings or the Library header chip).
  const [showArtPicker, setShowArtPicker] = useState(false);
  const handlePickArts = useCallback((a: Art[]) => {
    onChangeArts?.(a);
    setShowArtPicker(false);
  }, [onChangeArts]);

  // Warm the first Discover video once techniques are loaded, so opening the
  // Discover tab plays instantly instead of waiting on server extraction.
  const didPreloadDiscover = useRef(false);
  useEffect(() => {
    if (preview || didPreloadDiscover.current || techniques.length === 0) return;
    didPreloadDiscover.current = true;
    const id = setTimeout(() => { preloadFirstDiscover(techniques, statusMap); }, 1500);
    return () => clearTimeout(id);
  }, [preview, techniques, statusMap]);

  // Build a lookup map for overlay rendering
  const techMap = useMemo(() => {
    const map = new Map<string, Technique>();
    for (const t of techniques) map.set(t.id, t);
    return map;
  }, [techniques]);


  // Confirmation after adding a technique — the new card can be hidden by the
  // current view or filters, so the modal closing alone reads as a silent failure.
  const [addedToast, setAddedToast] = useState<string | null>(null);
  useEffect(() => {
    if (!addedToast) return;
    const id = setTimeout(() => setAddedToast(null), 4000);
    return () => clearTimeout(id);
  }, [addedToast]);

  // Toasts raised elsewhere (copying a share link, say) land in the same slot,
  // so there's one confirmation style and only ever one on screen.
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => onToast(setToast), []);
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(id);
  }, [toast]);

  const handleAddTechnique = useCallback((name: string, videoUrl: string, categoryId: string, tier: BeltRank, type: TechniqueType, uniform: Uniform, arts: Art[], attribution: string, context: string) => {
    const newTech: Technique = {
      id: `custom-${Date.now()}`,
      name,
      categoryId,
      tier,
      type,
      uniform,
      arts,
      ...(videoUrl ? { instagramUrl: videoUrl } : {}),
      ...(attribution ? { attribution } : {}),
      ...(context ? { context } : {}),
    };
    setCustomTechniques((prev) => {
      const next = [...prev, newTech];
      saveCustomTechniques(next);
      return next;
    });
    // Submit to Firestore for admin review
    if (user && videoUrl) {
      addDoc(firestoreCollection(db, 'audit_techniques'), {
        name,
        videoUrl,
        categoryId,
        tier,
        type,
        uniform,
        arts,
        // approveSubmission copies these onto the public technique as-is.
        ...(attribution ? { attribution } : {}),
        ...(context ? { context } : {}),
        submittedBy: user.uid,
        submitterName: user.displayName || user.email || user.uid,
        submittedAt: serverTimestamp(),
        status: 'pending',
      }).catch(() => {}); // fire-and-forget; user still has local copy
    }
    setAddModalCat(null);
    setAddedToast(name);
  }, [user]);

  const [showTutorial, setShowTutorial] = useState(false);
  // Tutorial disabled — its copy referred to the old tabs/drag flow that no
  // longer exists. Kept wired up (never triggered) so it can be revived later.
  const [tutorialStep, setTutorialStep] = useState<TutorialStep>(null);

  const handleTutorialStep = useCallback((step: TutorialStep) => {
    setTutorialStep(step);
    if (step === null) {
      localStorage.setItem('gnosis-tutorial-done', '1');
    }
  }, []);

  // Mouse-only for desktop + touch-only for mobile. Using PointerSensor +
  // TouchSensor together made them race on touch (pointer AND touch events both
  // fire), so a mid-gesture pointercancel could leave the drag — and the orb —
  // stuck. Mouse/Touch sensors each own a single input type, so the lifecycle
  // (including drag-end on release) fires cleanly.
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: { distance: 8 } // Desktop drag with mouse
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 100, // Quick response for drag handles
      tolerance: 8  // Reasonable tolerance
    }
  });
  const sensors = useSensors(mouseSensor, touchSensor);

  const lastPointer = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const track = (e: PointerEvent) => {
      lastPointer.current = { x: e.clientX, y: e.clientY };
      if (activeDragId) setDragPos({ x: e.clientX, y: e.clientY });
    };
    // On touch, the browser fires pointercancel once the drag is recognised and
    // stops sending pointermove — so the orb would freeze. touchmove keeps
    // firing throughout the gesture, so track it too to follow the finger.
    const trackTouch = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      lastPointer.current = { x: t.clientX, y: t.clientY };
      if (activeDragId) setDragPos({ x: t.clientX, y: t.clientY });
    };
    // Safety net: clear the orb the moment the finger/mouse lifts, so it can
    // never stick if dnd-kit's drag-end is missed. Only the genuine release
    // events — NOT pointercancel, which fires spuriously mid-touch-drag.
    const end = () => {
      if (!activeDragId) return;
      setActiveDragId(null);
      setDragPos(null);
      // Inline unlock (unlockScroll is declared later): release the scroll
      // container; body stays locked as it is for the app's lifetime.
      const sc = appRef.current?.querySelector('.scroll-container') as HTMLElement | null;
      if (sc) sc.style.overflow = '';
    };
    window.addEventListener('pointermove', track);
    window.addEventListener('touchmove', trackTouch, { passive: true });
    window.addEventListener('pointerup', end);
    window.addEventListener('touchend', end);
    return () => {
      window.removeEventListener('pointermove', track);
      window.removeEventListener('touchmove', trackTouch);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('touchend', end);
    };
  }, [activeDragId]);

  const lockScroll = useCallback(() => {
    const sc = appRef.current?.querySelector('.scroll-container') as HTMLElement | null;
    if (sc) sc.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }, []);
  const unlockScroll = useCallback(() => {
    const sc = appRef.current?.querySelector('.scroll-container') as HTMLElement | null;
    if (sc) sc.style.overflow = '';
    document.body.style.overflow = 'hidden'; // body stays locked (set on mount)
  }, []);

  const catMap = useMemo(() => {
    const map = new Map<string, typeof categories[0]>();
    for (const cat of categories) map.set(cat.id, cat);
    return map;
  }, []);

  // Poster handle by technique id, for the player's credit link. Looked up at
  // render time rather than carried on each PiP window, so restored windows
  // (rehydrated from localStorage) get the credit too.
  // Includes the admin queue and archive, not just the public pool: reviewing a
  // submission plays it in the same player, and those ids are absent from
  // `techniques`, so the credit line fell back to a bare "Instagram".
  const authorById = useMemo(
    () => new Map([...pendingSubs, ...archivedTechs, ...techniques].map((t) => [t.id, t.author])),
    [pendingSubs, archivedTechs, techniques]
  );
  /** Sport rules note per technique, for the arts in view. */
  const legalityById = useMemo(
    () => new Map([...pendingSubs, ...archivedTechs, ...techniques].map((t) => [t.id, legalityNote(t, rules, arts)])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingSubs, archivedTechs, techniques, rules, artsKey]
  );
  /** The athlete in the clip, when credited separately from the poster. */
  const attributionById = useMemo(
    () => new Map([...pendingSubs, ...archivedTechs, ...techniques].map((t) => [t.id, t.attribution])),
    [pendingSubs, archivedTechs, techniques]
  );

  // Tab-view handlers (My Mat / Journey): tap-to-play and tap-to-cycle-status,
  // the discoverable alternative to drag.
  const playTechnique = useCallback((tech: Technique) => {
    if (tech.instagramUrl) handlePipClick(tech.instagramUrl, tech.name, catMap.get(tech.categoryId)?.label, tech.id);
  }, [handlePipClick, catMap]);
  // A shared link lands here with a technique id. The pool arrives over a
  // snapshot, so this waits for it rather than firing on an empty list, and the
  // ref keeps a later snapshot from re-opening the player over the user.
  const openedShared = useRef(false);
  useEffect(() => {
    if (!initialTechniqueId || openedShared.current) return;
    // Wait on the snapshot itself, not on the pool being non-empty. The
    // library arrives asynchronously but locally-added techniques are read
    // from localStorage during the first render, so on any device that has one
    // the combined pool is already non-empty before the library lands — the
    // lookup missed, the ref burned, and the link opened nothing.
    if (dbLoading) return;
    // Search the unfiltered pool, not `techniques`: a shared link should open
    // whatever it names, even when the recipient's art selection (Judo only,
    // say) would otherwise hide it.
    const tech = [...dbTechniques, ...dedupedCustom].find((t) => t.id === initialTechniqueId);
    openedShared.current = true;
    // Opening the player is the whole point of arriving on a share link, and it
    // can only happen once the pool has loaded — so the state change belongs
    // here, not in an event handler. Guarded by the ref, so it runs once.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (tech) playTechnique(tech);
    else showToast("That clip isn't in the library");
  }, [initialTechniqueId, dbLoading, dbTechniques, dedupedCustom, playTechnique]);

  // Open one PiP per video in a group (a category card or collection) as a single
  // responsive grid. Only one group is shown at a time, so opening a new one
  // clears any previous group. Opened dormant so none autoplay.
  const openPipGroup = useCallback((techs: Technique[], title?: string) => {
    if (isMobile()) return;
    const items: PipVideo[] = techs
      .filter((t) => t.instagramUrl)
      .map((t) => ({ url: t.instagramUrl!, name: t.name, subtitle: catMap.get(t.categoryId)?.label, techniqueId: t.id }));
    if (items.length === 0) return;
    const groupId = `grp-${Date.now()}`;
    const group: PipGroup = { items, page: 0, title };
    setPipGroups({ [groupId]: group });
    setPipGroupOffsets({ [groupId]: groupPlacement(Math.min(items.length, PIP_PAGE_SIZE)) });
    // Drop any existing group's tiles (only one grid at a time), keep floating PiPs.
    setPipWindows((prev) => { const next = prev.filter((w) => !w.groupId); savePipWindows(next); return next; });
    showPipGroupPage(groupId, group);
  }, [catMap, savePipWindows, showPipGroupPage]);
  const openCollectionPips = useCallback((collectionId: string) => {
    const coll = collections.find((c) => c.id === collectionId);
    if (!coll) return;
    const ids = new Set(coll.techniqueIds);
    openPipGroup(techniques.filter((t) => ids.has(t.id)), coll.name);
  }, [collections, techniques, openPipGroup]);
  const cycleTechnique = useCallback((tech: Technique) => cycleStatus(tech.id), [cycleStatus]);
  // Star toggle on Library cards: on/off the mat (practice queue). Starring a
  // finished technique re-adds it to practicing.
  const toggleMatById = useCallback((id: string) => {
    setStatus(id, statusMap[id] === TechniqueStatus.MarkedForPractice
      ? TechniqueStatus.Unchecked
      : TechniqueStatus.MarkedForPractice);
  }, [setStatus, statusMap]);
  const toggleMat = useCallback((tech: Technique) => {
    toggleMatById(tech.id);
  }, [toggleMatById]);
  const closeFeed = useCallback(() => {
    setShowFeed(false);
    // Discover has no footer, so the feed's close (X) is the way out: go to Library.
    setActiveTab((t) => (t === 'discover' ? 'library' : t));
  }, []);

  // Discover shows the immersive feed directly (no intro screen).
  const feedTabRef = useRef<HTMLDivElement>(null);
  const scrollToHero = useCallback(() => {
    feedTabRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Keep the footer pinned in place on Discover (the feed fills the panel and
  // the bottom nav stays visible above it).
  useEffect(() => {
    if (activeTab === 'discover') appRef.current?.style.setProperty('--feed-scroll', '0');
  }, [activeTab]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(event.active.id as string);
    setDragPos(lastPointer.current);
    lockScroll();
  }, [lockScroll]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveDragId(null);
    setDragPos(null);
    unlockScroll();
    const { active, over } = event;

    if (over) {
      const techId = active.id as string;
      const collectionId = over.data.current?.collectionId as string | undefined;
      if (collectionId) {
        addToCollection(collectionId, techId);
        return;
      }
      const dropStatus = over.data.current?.status as TechniqueStatus | undefined;
      if (dropStatus) {
        setStatus(techId, dropStatus);
      }
    } else {
      // Dropped on no drop target. When a collection is open, dragging a card
      // out of the group area removes it from that collection (replaces the old
      // remove-from-collection button).
      if (activeCollectionId) {
        removeFromCollection(activeCollectionId, active.id as string);
      }
      if (!isMobile()) {
        const { x, y } = lastPointer.current;
        const panelRect = appRef.current?.getBoundingClientRect();
        const overPanel = panelRect && x >= panelRect.left && x <= panelRect.right && y >= panelRect.top && y <= panelRect.bottom;
        if (!overPanel) {
          const tech = techMap.get(active.id as string);
          if (tech?.instagramUrl) {
            const techCat = catMap.get(tech.categoryId);
            addPipWindow({ id: `${Date.now()}`, url: tech.instagramUrl, name: tech.name, subtitle: techCat?.label, techniqueId: tech.id, x: x - 140, y: y - 250 });
          }
        }
      }
    }
  }, [setStatus, techMap, addPipWindow, addToCollection, activeCollectionId, removeFromCollection, catMap]);

  // Category lookup by id

  // Group techniques by tier → categoryId
  const tierData = useMemo(() => {
    // tier → catId → Technique[]
    const map = new Map<number, Map<string, Technique[]>>();
    for (const tech of techniques) {
      let tierMap = map.get(tech.tier);
      if (!tierMap) { tierMap = new Map(); map.set(tech.tier, tierMap); }
      let list = tierMap.get(tech.categoryId);
      if (!list) { list = []; tierMap.set(tech.categoryId, list); }
      list.push(tech);
    }
    // Sort each tier's categories alphabetically by label, and items within each
    // category by type (grouped) then alphabetically by name.
    const catLabel = new Map(categories.map((c) => [c.id, c.label]));
    for (const tierMap of map.values()) {
      for (const list of tierMap.values()) {
        list.sort((a, b) => (TYPE_ORDER[a.type] - TYPE_ORDER[b.type]) || a.name.localeCompare(b.name));
      }
      const sortedEntries = Array.from(tierMap.entries()).sort(([aId], [bId]) =>
        (catLabel.get(aId) || aId).localeCompare(catLabel.get(bId) || bId)
      );
      tierMap.clear();
      for (const [k, v] of sortedEntries) tierMap.set(k, v);
    }
    return map;
  }, [techniques]);

  const catLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const cat of categories) map.set(cat.id, cat.label.toLowerCase());
    return map;
  }, []);

  const matchingTechIds = useMemo(() => {
    const query = searchQuery.toLowerCase();
    const fuzzyQuery = query.replace(/[^a-z0-9]/g, '');
    // The gym's own words for things (see data/lingo.ts): typing "honey hole"
    // or "outside sankaku" widens the search to the position and the wording
    // those clips are actually titled with.
    const lingo = lingoFor(query);
    const ids = new Set<string>();

    for (const tech of techniques) {
      const s = statusMap[tech.id];
      if (myMatOnly) {
        if (archivedView) {
          // Archived view: only the techniques the user has archived.
          if (s !== TechniqueStatus.Archived) continue;
        } else {
          // Favorites: only starred (practicing) or finished techniques.
          if (s !== TechniqueStatus.MarkedForPractice && s !== TechniqueStatus.Completed) continue;
        }
      } else if (s === TechniqueStatus.Archived) {
        // Library hides archived techniques — the user is done with them.
        continue;
      }
      // Matched by rung name, not by number: picking Beginner on the neutral
      // scale has to keep both of the tiers that carry that name. No pick means
      // every level, same as the Position and Type rows.
      if (selectedRankLabels.size > 0 && !selectedRankLabels.has(tierLabels[tech.tier])) continue;
      if (uniformFilter !== 'all') {
        // Untagged techniques are treated as Gi.
        const u = tech.uniform ?? Uniform.Gi;
        if (u !== uniformFilter) continue;
      }
      if (categoryFilter.size > 0 && !categoryFilter.has(tech.categoryId)) continue;
      if (typeFilter.size > 0 && !typeFilter.has(tech.type)) continue;
      if (hiddenByRules(tech, rules, arts, showBanned)) continue;
      const catLabel = catLabelMap.get(tech.categoryId) ?? '';
      // Creator matching is fuzzy: strip everything but letters/digits from
      // both sides so "danaher bjj" or "@danaher.bjj" finds "danaher_bjj",
      // and handles match attribution names ("B-Team" ~ "b team").
      const creator = `${tech.author ?? ''} ${tech.attribution ?? ''}`.toLowerCase().replace(/[^a-z0-9]/g, '');
      const matchesCreator = fuzzyQuery !== '' && creator.includes(fuzzyQuery);
      const matchesAlias = [tech.japaneseName, ...(tech.aliases ?? [])].some((a) => a?.toLowerCase().includes(query));
      const matchesLingoTerm = lingo.some((e) => matchesLingo(e, tech));
      const matchesSearch = !query || tech.name.toLowerCase().includes(query) || matchesAlias || matchesLingoTerm || catLabel.includes(query) || matchesCreator;

      // Library is a pure browse view now (no status tabs) — show every
      // technique that matches the search / category / type filters,
      // regardless of practice status.
      if (matchesSearch) ids.add(tech.id);
    }

    return ids;
  }, [searchQuery, categoryFilter, typeFilter, selectedRankLabels, uniformFilter, showBanned, rules, arts, myMatOnly, archivedView, statusMap, techniques, catLabelMap, tierLabels]);

  // The belt filter is now part of matchingTechIds, so this is just the total.
  const visibleMatchCount = matchingTechIds.size;

  // The Ontology mind map shows the same set the other pages compute via the
  // shared filter interface (matchingTechIds already applies the active
  // category/type/belt/uniform/search filters and hides archived techniques).
  const ontologyTechniques = useMemo(
    () => techniques.filter((t) => matchingTechIds.has(t.id)),
    [techniques, matchingTechIds]
  );

  // Build overlay content
  const activeTech = activeDragId ? techMap.get(activeDragId) : null;
  const activeTier = activeTech ? activeTech.tier : null;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
  }, []);

  // Demo cursor bubble — each tap gets a unique key to restart the animation
  const [, setDemoCursorRaw] = useState<{ x: number; y: number; visible: boolean; key: number }>({ x: 0, y: 0, visible: false, key: 0 });
  const setDemoCursor = useCallback((c: { x: number; y: number; visible: boolean }) => {
    const withKey = { ...c, key: Date.now() };
    setDemoCursorRaw(withKey);
    onDemoCursor?.(withKey);
  }, [onDemoCursor]);

  // Splash overlay for demo loop (visible in preview mode)
  const [demoSplash, setDemoSplash] = useState(preview && !captureMode);

  // Auto-pilot demo loop for preview mode
  useEffect(() => {
    if (!preview || captureMode) return;

    const pistolTech = techniques.find((t) => t.name.toLowerCase().includes('pistol grip'));
    // Only categories that have WhiteBelt techniques, sorted alphabetically
    const whiteBeltCatIds = new Set(
      techniques.filter((t) => t.tier === BeltRank.WhiteBelt).map((t) => t.categoryId)
    );
    const allKeys = [...categories]
      .filter((c) => whiteBeltCatIds.has(c.id))
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((c) => `${c.id}-${BeltRank.WhiteBelt}`);

    let cancelled = false;
    const sleep = (ms: number) => new Promise<void>((resolve) => {
      const t = setTimeout(resolve, ms);
      if (cancelled) clearTimeout(t);
    });

    const tapEl = async (sel: string, delay = 800) => {
      const el = appRef.current?.querySelector<HTMLElement>(sel);
      const container = appRef.current;
      if (!el || !container || cancelled) return;

      const cr = container.getBoundingClientRect();
      const er = el.getBoundingClientRect();
      const scaleX = container.offsetWidth / cr.width;
      const scaleY = container.offsetHeight / cr.height;
      const x = (er.left + er.width / 2 - cr.left) * scaleX;
      const y = (er.top + er.height / 2 - cr.top) * scaleY;

      setDemoCursor({ x, y, visible: true });
      await sleep(700);
      setDemoCursorRaw((c) => ({ ...c, visible: false }));
      onDemoCursor?.({ x: 0, y: 0, visible: false });
      if (delay > 700) await sleep(delay - 700);
    };

    // Type text into search input character by character
    const typeSearch = async (text: string) => {
      for (let i = 0; i <= text.length; i++) {
        if (cancelled) return;
        setSearchQuery(text.slice(0, i));
        await sleep(80);
      }
    };

    const run = async () => {
      while (!cancelled) {
        // ── 1. Splash screen ──
        (window as Window & { __demoPhase?: string }).__demoPhase = 'splash-start';
        setDemoSplash(true);
        await sleep(800);
        if (cancelled) return;
        setDemoSplash(false);
        (window as Window & { __demoPhase?: string }).__demoPhase = 'browsing';
        await sleep(400);

        // ── 2. Reset ALL state to initial ──
        const scrollEl = appRef.current?.querySelector('.scroll-container');
        setSearchQuery('');
        setStatusFilter('all');
        setCategoryFilter(new Set<string>());
        setTypeFilter(new Set<string>());
        setShowFilter(false);
        setShowFeed(false);
        setSoloVideo(null);
        setPipWindows([]);
        // Reset technique status (un-favorite anything we starred)
        if (pistolTech) setStatus(pistolTech.id, TechniqueStatus.Unchecked);
        catAccordion.closeAll();
        scrollEl?.scrollTo({ top: 0 });
        await sleep(1000);

        // ── 3. Scroll through techniques, expanding some sections ──
        if (cancelled) return;
        // Expand categories by toggling the actual key, then tap the matching card
        const visibleCards = () => appRef.current?.querySelectorAll<HTMLElement>('.category-card') ?? [];
        for (let i = 0; i < Math.min(2, allKeys.length); i++) {
          if (cancelled) return;
          const cards = visibleCards();
          if (cards[i]) {
            cards[i].scrollIntoView({ behavior: 'smooth', block: 'center' });
            await sleep(900); // wait for smooth scroll to settle before tapping
          }
          await tapEl(`.category-card:nth-child(${i + 1}) .category-card-header`, 400);
          catAccordion.open(allKeys[i]);
          await sleep(1200);
        }

        // Scroll down slowly
        if (scrollEl) {
          const maxScroll = scrollEl.scrollHeight - scrollEl.clientHeight;
          scrollEl.scrollTo({ top: maxScroll, behavior: 'smooth' });
          await sleep(3000);
        }
        await sleep(1000);

        // ── 4. Smooth-scroll back to the top so the user sees the scroll
        //      animation, then close accordions and search for "pistol grip" ──
        if (cancelled) return;
        // Smooth scroll mirrors the earlier scroll-down animation — feels
        // like natural UI browsing rather than an instant teleport.
        scrollEl?.scrollTo({ top: 0, behavior: 'smooth' });
        await sleep(1800); // let the smooth scroll complete

        // Belt-and-suspenders: ensure we're truly at 0 before proceeding,
        // in case the smooth scroll was interrupted or didn't fully settle.
        {
          const el = appRef.current?.querySelector<HTMLElement>('.scroll-container');
          if (el && el.scrollTop !== 0) el.scrollTop = 0;
        }

        // Now close the expanded accordions — doing this AFTER the scroll is
        // complete avoids the scrollHeight-shrinking race that made the
        // earlier instant-scroll attempts unreliable.
        catAccordion.closeAll();
        await sleep(500);

        await tapEl('.search-input', 400);
        await typeSearch('pistol grip');
        await sleep(1500);

        // ── 5. Open the result category and play video in PiP ──
        if (cancelled) return;
        const pistolKey = pistolTech ? `${pistolTech.categoryId}-${pistolTech.tier}` : allKeys[0];
        await tapEl('.category-card-header', 400);
        catAccordion.open(pistolKey);
        await sleep(800);

        if (pistolTech?.instagramUrl) {
          await tapEl('.video-link', 400);
          const catLabel = categories.find((c) => c.id === pistolTech.categoryId)?.label;
          setSoloVideo({ url: pistolTech.instagramUrl, name: pistolTech.name, subtitle: catLabel, techniqueId: pistolTech.id });
          await sleep(6000);

          // ── 6. Favorite from PiP ──
          if (cancelled) return;
          setStatus(pistolTech.id, TechniqueStatus.MarkedForPractice);
          await sleep(1500);

          // ── 7. Close PiP ──
          setSoloVideo(null);
          await sleep(500);
        }

        // ── 8. Clean up search state and close the pistol category ──
        if (cancelled) return;
        setSearchQuery('');
        catAccordion.close(pistolKey);
        await sleep(500);

        // ── 9. Open Discovery feed ──
        if (cancelled) return;
        const freshScrollForFeed = appRef.current?.querySelector<HTMLElement>('.scroll-container');
        if (freshScrollForFeed) freshScrollForFeed.scrollTop = 0;
        await sleep(400);
        await tapEl('.feed-banner', 400);
        setShowFeed(true);
        await sleep(5000); // let the first video load and play

        // Advance to a second video
        if (cancelled) return;
        const feedViewport1 = appRef.current?.querySelector<HTMLElement>('.feed-viewport');
        feedViewport1?.dispatchEvent(new Event('feed-advance'));
        await sleep(3500);

        // Tap the favorite (star) button with a visible cursor so it's clear
        // this is where a user would mark the video as a technique to practice.
        if (cancelled) return;
        await tapEl('.feed-star-btn', 1200);
        await sleep(400);

        // Advance one more time to show the feed continues after favoriting
        if (cancelled) return;
        const feedViewport2 = appRef.current?.querySelector<HTMLElement>('.feed-viewport');
        feedViewport2?.dispatchEvent(new Event('feed-advance'));
        await sleep(3000);

        // Tap the close button with a visible cursor, then actually close
        if (cancelled) return;
        await tapEl('.pip-close-btn', 800);
        setShowFeed(false);
        await sleep(600);

        // Fade splash over the feed for seamless loop
        setDemoSplash(true);
        (window as Window & { __demoPhase?: string }).__demoPhase = 'loop-end';
        await sleep(800);

        // Loop restarts — reset at top handles cleanup
      }
    };

    const timeout = setTimeout(() => run(), 500);
    return () => { cancelled = true; clearTimeout(timeout); };
  }, [preview]);

  // How many filters are active (for the Discover toggle badge / "filtered" dot,
  // and whether the pills row renders at all). A narrowed art selection counts
  // one per art, matching the pills it produces.
  const activeArtFilterCount = !allArtsSelected && onChangeArts ? arts.length : 0;
  const activeFilterCount = activeArtFilterCount + categoryFilter.size + typeFilter.size + rankFilter.size + (uniformFilter !== 'all' ? 1 : 0) + (showBanned ? 1 : 0);


  // Whether the Library toolbar is stuck to the top (user has scrolled into the
  // technique list). When stuck we collapse the filter bar into a compact filter
  // button (with a count bubble) sitting in the sticky search row.
  const [libScrolled, setLibScrolled] = useState(false);
  const onLibScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const sc = e.currentTarget;
    const searchRow = sc.querySelector('.search-row') as HTMLElement | null;
    const toolbar = sc.querySelector('.library-toolbar') as HTMLElement | null;
    if (!searchRow || !toolbar) return;
    // The search row is now a sibling above the toolbar (so it can stay pinned
    // for the whole list). Subtract its height so "stuck" still triggers exactly
    // when the search row reaches the top — i.e. when the toolbar below it has
    // scrolled up to the search row's resting bottom.
    const scTop = sc.getBoundingClientRect().top;
    const searchH = searchRow.getBoundingClientRect().height;
    const gap = toolbar.getBoundingClientRect().top - scTop - searchH;
    // Hysteresis: stick once the search row reaches the top, but only release
    // after scrolling back up past a deadband. Collapsing the filter bar shortens
    // the toolbar, which would otherwise flip the state right at the threshold and
    // make the scroll bounce.
    setLibScrolled((prev) => (prev ? gap <= 28 : gap <= 0));
  }, []);

  // Filter and Add are mutually exclusive — opening one closes the other.
  const toggleFilters = useCallback(() => {
    setAddModalCat(null);
    setFilterScope('all');
    setShowFilter((v) => !v);
  }, []);
  const openAddTechnique = useCallback(() => {
    setShowFilter(false);
    setAddModalCat({ id: categories[0].id, label: categories[0].label, tier: BeltRank.WhiteBelt });
  }, []);

  // Active filters as actionable entries (icon + label + remove), rendered as
  // removable pills in the Library filter row when the panel is collapsed.
  // `tone` picks the pill's colour so each filter reads as belonging to its
  // section (see .filter-tone--* in App.css).
  const activeFilters: { key: string; label: string; tone: string; Icon?: IconComponent; onRemove: () => void }[] = [
    // Art leads, matching its position at the top of the filter panel. Only a
    // narrowed selection counts as a filter — every art selected is "All".
    ...(!allArtsSelected && onChangeArts
      ? arts.map((a) => ({ key: `art-${a}`, label: ART_META[a].shortLabel, tone: 'art', Icon: Swords, onRemove: () => removeArtFilter(a) }))
      : []),
    ...[...rankFilter].map((t) => ({ key: `rank-${t}`, label: tierLabels[t], tone: 'level', onRemove: () => toggleRank(t) })),
    ...(uniformFilter !== 'all' ? [{ key: 'uniform', label: uniformFilter === Uniform.Gi ? 'Gi' : 'No-Gi', tone: 'uniform', Icon: Shirt, onRemove: () => setUniformFilter('all') }] : []),
    ...(showBanned ? [{ key: 'banned', label: 'Show banned', tone: 'rules', Icon: TriangleAlert, onRemove: () => setShowBanned(false) }] : []),
    ...categories.filter((c) => categoryFilter.has(c.id)).map((c) => ({ key: `cat-${c.id}`, label: c.label, tone: 'position', Icon: CATEGORY_ICONS[c.id], onRemove: () => toggleCategory(c.id) })),
    ...Object.entries(TechniqueType).filter(([, v]) => typeFilter.has(v)).map(([label, v]) => ({ key: `type-${v}`, label, tone: 'type', Icon: TYPE_ICONS[v], onRemove: () => toggleType(v) })),
  ];

  // Shared filter chips, reused by the Library toolbar and the Discover hero.
  // Picks never collapse the panel — Position, Type and Art are all
  // multi-select, so it has to stay open across several taps. "Back to results"
  // is the only way out.
  const filterPanel = () => {
    // A section shows only when it's in scope AND has something to offer for
    // the selected arts — pick Judo alone and there are no positions, so the
    // Position section is dropped rather than left as a lone "All" chip.
    const hasOptions = { position: visibleCategories.length > 0, type: availableTypes.size > 0, level: RANK_OPTIONS.length > 1 };
    const show = (s: 'position' | 'type' | 'level') => (filterScope === 'all' || filterScope === s) && hasOptions[s];
    return (
    <div className="filter-panel">
      {/* Art comes first — it re-scopes every section below it. Deliberately
          doesn't collapse the panel on pick (unlike the others) so you can see
          the position/type lists update as you change arts. */}
      {filterScope === 'all' && onChangeArts && (
      <div className="filter-panel-section">
        <div className="filter-panel-row">
          <div className="filter-panel-header">
            <Swords size={12} />
            <span>Sport</span>
          </div>
          <div className="category-filter-bar filter-tone--art">
            <button
              className={`category-filter-chip category-filter-chip--toggle${allArtsSelected ? ' category-filter-chip--active' : ''}`}
              onClick={() => onChangeArts(ARTS.map((a) => a.id))}
            >
              All
            </button>
            {/* While "All" is lit the individual chips read as unselected, so
                the row doesn't show every art highlighted at once — same
                convention as the Position and Type rows. */}
            {ARTS.map((a) => (
              <button
                key={a.id}
                className={`category-filter-chip${!allArtsSelected && arts.includes(a.id) ? ' category-filter-chip--active' : ''}`}
                onClick={() => toggleArtFilter(a.id)}
              >
                {a.shortLabel}
              </button>
            ))}
          </div>
        </div>
      </div>
      )}
      {show('position') && (
      <div className="filter-panel-section">
        <div className="filter-panel-row">
          {filterScope === 'all' && (
          <div className="filter-panel-header">
            <LayoutGrid size={12} />
            <span>Position</span>
          </div>
          )}
          <div className="category-filter-bar filter-tone--position">
            <button
              className={`category-filter-chip category-filter-chip--toggle${categoryFilter.size === 0 ? ' category-filter-chip--active' : ''}`}
              onClick={() => { setCategoryFilter(new Set<string>()); }}
            >
              All
            </button>
            {visibleCategories.map((cat) => {
              const Icon = CATEGORY_ICONS[cat.id];
              return (
                <button
                  key={cat.id}
                  className={`category-filter-chip${categoryFilter.has(cat.id) ? ' category-filter-chip--active' : ''}`}
                  onClick={() => { toggleCategory(cat.id); }}
                >
                  {Icon && <Icon size={13} style={{ marginRight: 5, verticalAlign: '-2px' }} />}
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      )}
      {show('type') && (
      <div className="filter-panel-section">
        <div className="filter-panel-row">
          {filterScope === 'all' && (
          <div className="filter-panel-header">
            <Shapes size={12} />
            <span>Type</span>
          </div>
          )}
          <div className="category-filter-bar filter-tone--type">
            <button
              className={`category-filter-chip category-filter-chip--toggle${typeFilter.size === 0 ? ' category-filter-chip--active' : ''}`}
              onClick={() => { setTypeFilter(new Set<string>()); }}
            >
              All
            </button>
            {Object.entries(TechniqueType).filter(([, value]) => availableTypes.has(value)).map(([label, value]) => {
              const Icon = TYPE_ICONS[value];
              return (
                <button
                  key={value}
                  className={`category-filter-chip${typeFilter.has(value) ? ' category-filter-chip--active' : ''}`}
                  onClick={() => { toggleType(value); }}
                >
                  {Icon && <Icon size={13} style={{ marginRight: 5, verticalAlign: '-2px' }} />}
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      )}
      {show('level') && (
      <div className="filter-panel-section">
        <div className="filter-panel-row">
          {filterScope === 'all' && (
          <div className="filter-panel-header">
            <Layers size={12} />
            <span>Level</span>
          </div>
          )}
          <div className="category-filter-bar filter-tone--level">
            {RANK_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`category-filter-chip${opt.value === 'all' ? ' category-filter-chip--toggle' : ''}${opt.value === 'all' ? (rankFilter.size === 0 ? ' category-filter-chip--active' : '') : (selectedRankLabels.has(tierLabels[opt.value]) ? ' category-filter-chip--active' : '')}`}
                onClick={() => { if (opt.value === 'all') setRankFilter(new Set()); else toggleRank(opt.value); }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      )}
      {filterScope === 'all' && showUniformFilterFor(arts) && (
      <div className="filter-panel-section">
        <div className="filter-panel-row">
          <div className="filter-panel-header">
            <Shirt size={12} />
            <span>Uniform</span>
          </div>
          <div className="category-filter-bar filter-tone--uniform">
            {UNIFORM_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`category-filter-chip${uniformFilter === opt.value ? ' category-filter-chip--active' : ''}`}
                onClick={() => { setUniformFilter(opt.value); }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      )}
      {/* Sport rules: a move banned in the one art selected is hidden; this
          shows it anyway. With two arts selected nothing is hidden, so the
          section stays out of the way. */}
      {filterScope === 'all' && arts.length === 1 && (
      <div className="filter-panel-section">
        <div className="filter-panel-row">
          <div className="filter-panel-header">
            <TriangleAlert size={12} />
            <span>Rules</span>
          </div>
          <div className="category-filter-bar filter-tone--rules">
            <button
              className={`category-filter-chip${showBanned ? ' category-filter-chip--active' : ''}`}
              onClick={() => { setShowBanned((v) => !v); }}
            >
              Show banned
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
    );
  };


  // A creator Home has been asked to show (see openCreator below).
  const [creatorRequest, setCreatorRequest] = useState<{ handle: string; n: number } | null>(null);
  // The How To screen lives on Home; every header's ? goes there and opens it.
  const [howToRequest, setHowToRequest] = useState(0);
  const helpAction = (
    <div className="home-header-btns">
      <button
        type="button"
        className="changelog-btn"
        onClick={() => { setActiveTab('home'); setHowToRequest((n) => n + 1); }}
        aria-label="How To"
        title="How To"
      >
        <CircleHelp size={20} />
      </button>
    </div>
  );

  // Tab bar navigation. Also what a guide's live control uses to land the user
  // on the screen where its result shows.
  const goTab = useCallback((tab: TabId) => {
    // A guest can browse the library and watch their free clips; anything that
    // belongs to an account asks for one instead of rendering empty.
    if (guest && (tab === 'favorites' || tab === 'settings')) {
      setWallReason('locked');
      return;
    }
    // Tapping Discover always resets to the main Discover view (the
    // hero/intro) — scroll back to the top when already on the tab.
    if (tab === 'discover') scrollToHero();
    // New view starts at the top, so the toolbar isn't stuck yet.
    setLibScrolled(false);
    // Always land on Favorites (not its Archived sub-view) when tabbing.
    setArchivedView(false);
    setActiveCollectionId(null);
    // Tabbing (e.g. the library icon) exits the admin-only
    // pending/archived list back to the normal library.
    setAdminViewMode(null);
    // Leaving Home drops any creator it was asked to show, so the ask doesn't
    // replay the next time Home mounts.
    if (tab !== 'home') { setCreatorRequest(null); setHowToRequest(0); }
    setActiveTab(tab);
  }, [scrollToHero, guest]);

  // A player's credit line: land on the creator's page in Home. Home keeps
  // the app's own view of a creator (their clips, then the way out to
  // Instagram); the player itself stays open.
  const openCreator = useCallback((handle: string) => {
    goTab('home');
    setCreatorRequest((r) => ({ handle, n: (r?.n ?? 0) + 1 }));
  }, [goTab]);

  // The guides' live controls (see GuideBridge in Forum.tsx). Each lands in
  // Library with only the guide's own narrowing active, so what the reader
  // sees is exactly what they asked for.
  const clearLibraryFilters = useCallback(() => {
    setSearchQuery('');
    setCategoryFilter(new Set());
    setTypeFilter(new Set());
    setRankFilter(new Set());
    setUniformFilter('all');
    setShowFilter(false);
  }, []);
  const guideBridge = useMemo(() => ({
    // Until the catalog lands there's nothing to scope the list by; the full
    // list beats an empty row.
    positions: visibleCategories.length > 0 ? visibleCategories : categories,
    search: (query: string) => { clearLibraryFilters(); setSearchQuery(query); goTab('library'); },
    filterPosition: (id: string) => { clearLibraryFilters(); setCategoryFilter(new Set([id])); goTab('library'); },
    openDiscover: () => goTab('discover'),
    createCollection: (name: string) => {
      const id = `c-${Date.now()}`;
      const trimmed = name.trim();
      if (!trimmed) return;
      mutateCollections((prev) => [...prev, { id, name: trimmed, techniqueIds: [] }]);
      clearLibraryFilters();
      goTab('favorites');
      setActiveCollectionId(id);
    },
    openAdd: openAddTechnique,
  }), [visibleCategories, clearLibraryFilters, goTab, mutateCollections, openAddTechnique]);

  return (
    <>
    <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => { setActiveDragId(null); setDragPos(null); unlockScroll(); }}>
      <div ref={appRef} className={`app-container${tutorialStep && tutorialStep !== 'welcome' ? ' tutorial-active' : ''}${preview ? '' : ' app-container--live'}${activeTab === 'discover' && !preview ? ' discover-active' : ''}`} style={preview ? { position: 'relative' } : undefined}>
        {preview && (
          <div className={`demo-splash${demoSplash ? '' : ' demo-splash--hidden'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" width="80" height="80">
              <circle cx="12" cy="12" r="4" fill="#863bff" opacity="0.9"/>
              <circle cx="6" cy="7" r="2.5" fill="#863bff" opacity="0.55"/>
              <circle cx="18" cy="7" r="2" fill="#863bff" opacity="0.5"/>
              <circle cx="5" cy="16" r="1.5" fill="#863bff" opacity="0.45"/>
              <circle cx="19" cy="15" r="3" fill="#863bff" opacity="0.6"/>
              <circle cx="12" cy="4" r="1.5" fill="#863bff" opacity="0.45"/>
              <circle cx="8" cy="19" r="2" fill="#863bff" opacity="0.55"/>
            </svg>
          </div>
        )}
        {preview && !captureMode && (
          <div className="top-bar">
            <button className="feed-banner" onClick={() => setShowFeed(true)}>
              <Compass size={15} className="feed-banner-icon" />
              <span className="feed-banner-text">Enter Discovery Mode</span>
            </button>
          </div>
        )}

        <TutorialOverlay
          visible={showTutorial}
          step={tutorialStep}
          onDismiss={() => { setShowTutorial(false); handleTutorialStep(null); }}
          onStepChange={handleTutorialStep}
        />

        {showFeed && (isMobile() || preview) ? (
          // Preview/banner-triggered immersive overlay feed.
          <FeedPlayer
            techniques={techniques}
            statusMap={statusMap}
            onClose={closeFeed}
            onToggleStar={toggleMatById}
            muted={preview}
          />
        ) : activeTab === 'discover' ? (
          // An intro modal covers the first clip until the user swipes it up,
          // which starts playback (see FeedPlayer's `intro`). It carries the
          // shared filter panel, and those filters narrow the feed's pool — so
          // what you pick there is what you swipe through.
          // No art-specific swap here: an art with no content is just an empty
          // pool, which the feed reports as end-of-content in its normal shell.
          (
            <FeedPlayer
              techniques={techniques}
              statusMap={statusMap}
              onClose={closeFeed}
              onToggleStar={toggleMatById}
              inline
              muted={preview}
              guest={guest}
              onPreviewExhausted={() => setWallReason('plays')}
              filterCategoryIds={[...categoryFilter]}
              filterTypeIds={[...typeFilter]}
              filterRanks={rankFilter.size === 0 ? undefined : TIERS.filter((t) => selectedRankLabels.has(tierLabels[t]))}
              intro={
                <>
                  <ViewHeader
                    title="Discover"
                    className="view-header--home"
                    action={helpAction}
                    icon={<Compass size={34} strokeWidth={2} color="#a06bff" />}
                  />
                  {/* .feed-intro-scroll marks this as the modal's scrollable
                      region — FeedPlayer lets gestures here scroll or tap
                      instead of dismissing the modal. */}
                  <div className="feed-intro-scroll">
                    {/* The filter button swaps the two views rather than
                        stacking them: collapsed shows the active pills,
                        expanded replaces them with the full panel. */}
                    <div className="active-filter-pills feed-intro-summary">
                      <button
                        className={`tier-filter-toggle${discoverFilterOpen ? ' tier-filter-toggle--active' : ''}`}
                        onClick={() => setDiscoverFilterOpen((v) => !v)}
                        aria-expanded={discoverFilterOpen}
                        aria-label={discoverFilterOpen ? 'Hide filters' : 'Filter and view options'}
                        title={discoverFilterOpen ? 'Hide filters' : 'Filter and view options'}
                      >
                        <Filter size={16} />
                      </button>
                      {!discoverFilterOpen && (activeFilterCount > 0 ? (
                        activeFilters.map(({ key, label, tone, Icon, onRemove }) => (
                          <button key={key} className={`active-filter-pill filter-tone--${tone}`} onClick={onRemove} aria-label={`Remove ${label} filter`}>
                            {Icon && <Icon size={13} />}
                            <span>{label}</span>
                            <X size={13} className="active-filter-pill-x" />
                          </button>
                        ))
                      ) : (
                        <button type="button" className="no-active-filters" onClick={() => setDiscoverFilterOpen(true)}>No Active Filters</button>
                      ))}
                    </div>
                    {discoverFilterOpen && filterPanel()}
                  </div>
                </>
              }
            />
          )
        ) : showFeed ? (
          <FeedPlayer
            techniques={techniques}
            statusMap={statusMap}
            onClose={closeFeed}
            onToggleStar={toggleMatById}
            inline
            muted={preview}
          />
        ) : activeTab === 'settings' ? (
          <div className="page-view">
            <ViewHeader
              title="Settings"
              className="view-header--home"
              action={helpAction}
              icon={<Settings size={34} strokeWidth={2} color="#a06bff" />}
            />
            <div className="page-view-body">
              <SettingsModal
                user={user}
                sub={sub}
                inline
                onClose={closeOverlayTab}
                onLogOut={logOut}
                pendingCount={pendingSubs.length}
                archivedCount={archivedTechs.length}
                onAdminView={(mode) => {
                  setAdminViewMode(mode);
                  setLibScrolled(false);
                  setActiveTab('library');
                }}
              />
            </div>
          </div>
        ) : activeTab === 'ontology' ? (
          <div className="ontology-view">
            <ViewHeader
              title="Paths"
              className="view-header--home"
              action={helpAction}
              icon={<Network size={34} strokeWidth={2} color="#a06bff" />}
            />
            {/* The position graph is curated BJJ content (positionLinks.ts) —
                other arts get the coming-soon state until they have their own. */}
            {!hasBjj ? (
              <ArtEmptyState arts={arts} onSwitchArt={() => setShowArtPicker(true)} />
            ) : (
            <>
            {/* Same filter interface as the Library: a toggle + active pills,
                with the shared filter panel; the map shows the filtered set. */}
            <div className="active-filter-pills">
              {!showFilter && (
                <button
                  className="tier-filter-toggle"
                  onClick={toggleFilters}
                  aria-label="Filter and view options"
                  title="Filter and view options"
                >
                  <Filter size={16} />
                </button>
              )}
              {showFilter && (
                <button type="button" className="filter-back-chip" onClick={toggleFilters} aria-label="Close filters">
                  <ChevronLeft size={14} />
                  <span>Back</span>
                </button>
              )}
              {activeFilterCount > 0 ? (
                activeFilters.map(({ key, label, tone, Icon, onRemove }) => (
                  <button key={key} className={`active-filter-pill filter-tone--${tone}`} onClick={onRemove} aria-label={`Remove ${label} filter`}>
                    {Icon && <Icon size={13} />}
                    <span>{label}</span>
                    <X size={13} className="active-filter-pill-x" />
                  </button>
                ))
              ) : (
                <button type="button" className="no-active-filters" onClick={toggleFilters}>No Active Filters</button>
              )}
            </div>
            {showFilter ? (
              <div className="ontology-panel-wrap">{filterPanel()}</div>
            ) : (
              <Ontology techniques={ontologyTechniques} statusMap={statusMap} onPlay={playTechnique} onFilterCategory={toggleCategory} categoryFilter={categoryFilter} />
            )}
            </>
            )}
          </div>
        ) : activeTab === 'help' && user ? (
          <div className="page-view">
            <ViewHeader
              title="Help"
              className="view-header--home"
              action={helpAction}
              icon={<MessageCircle size={34} strokeWidth={2} color="#a06bff" />}
            />
            <div className="page-view-body">
              {user.uid === 'bZj8y9DApEac3eKOuKV08MKcaDx2'
                ? <AdminChat user={user} />
                : <ChatWidget user={user} inline onClose={closeOverlayTab} />}
            </div>
          </div>
        ) : activeTab === 'home' && (user || captureMode || guest) ? (
          // Neither capture mode nor the signed-out preview has a real user; a
          // stub lets Home render for both. The rails a guest can't read come
          // from the public copy instead (see guestMode.tsx).
          <Home
            user={user ?? ({ uid: 'capture-preview', displayName: 'Guest', email: null } as unknown as NonNullable<typeof user>)}
            library={{
              view: libraryView,
              onToggleView: toggleLibraryView,
              statusOf: (id) => statusMap[id] || TechniqueStatus.Unchecked,
              isWatched: (t) => (t.instagramUrl ? watchedUrls.has(t.instagramUrl) : false),
              recentlyWatched: [...watchedUrls].reverse(),
              onPlay: (t) =>
                t.instagramUrl &&
                handlePipClick(t.instagramUrl, t.name, catLabelMap.get(t.categoryId) ?? '', t.id),
              onToggleMat: (t) => toggleMat(t),
              onInfo: (t) => setInfoTech(t),
              onVersions: (g) => setVersionGroup(g),
              onOpenAllVersions: (g) => openPipGroup(g.variants, g.name),
            }}
            guide={guideBridge}
            creatorRequest={creatorRequest}
            howToRequest={howToRequest}
          />
        ) : activeTab === 'journey' ? (
          // Series are curated BJJ groupings (Kimura, Closed Guard…) — for
          // other arts show the coming-soon state until per-art series exist
          // (Series.arts is the seam for lifting this).
          !hasBjj ? (
            <div className="page-view">
              <ViewHeader
                title="Series"
                className="view-header--home"
                action={helpAction}
                icon={<Waypoints size={34} strokeWidth={2} color="#a06bff" />}
              />
              <ArtEmptyState arts={arts} onSwitchArt={() => setShowArtPicker(true)} />
            </div>
          ) : (
          <Journey
            techniques={techniques}
            statusMap={statusMap}
            watchedUrls={watchedUrls}
            onPlay={playTechnique}
            onCycle={cycleTechnique}
            onSetStatus={setStatus}
          />
          )
        ) : (
        <div className={`scroll-container${myMatOnly ? ' scroll-container--favorites' : ''}${showFilter ? ' scroll-container--filtering' : ''}`} onScroll={onLibScroll}>
          <ViewHeader
            title={archivedView ? 'Archived' : myMatOnly ? 'Favorites' : 'Library'}
            className="view-header--home"
            action={helpAction}
            icon={archivedView ? <Archive size={34} strokeWidth={2} color="#a06bff" /> : myMatOnly ? <Star size={34} strokeWidth={2} color="#a06bff" /> : <Library size={34} strokeWidth={2} color="#a06bff" />}
          />
          <div className="search-row" style={{ padding: '8px', display: 'flex', gap: 8, alignItems: 'center' }}>
            {showFilter ? (
              // While the filter panel is open the search field does nothing (the
              // list is hidden), so it gives up its slot to the Back control — the
              // real exit, now where the eye already is.
              <button type="button" className="filter-back-chip filter-back-chip--wide" onClick={toggleFilters} aria-label="Close filters">
                <ChevronLeft size={16} />
                <span>Back to results</span>
              </button>
            ) : (
              <div className="search-input-wrapper" style={{ flex: 1 }}>
                <Search size={14} className="search-icon" style={{ left: 10 }} />
                <input
                  type="text"
                  className="search-input"
                  placeholder={archivedView ? 'Find an archived technique' : myMatOnly ? 'Find a favorite' : 'Find a technique'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            )}
            {/* The filter toggle lives here (left of Add, with a count bubble)
                whenever the pills row below isn't carrying it: when scrolled, or
                when there are no active filters (then we skip the pills row
                entirely). Hidden while the panel is open — Back owns the row. */}
            {!showFilter && (libScrolled || activeFilterCount === 0) && (
              <button
                className="tier-filter-toggle filter-toggle--badged"
                onClick={toggleFilters}
                aria-label="Filter and view options"
                title="Filter and view options"
              >
                <Filter size={16} />
                {activeFilterCount > 0 && <span className="filter-count-bubble">{activeFilterCount}</span>}
              </button>
            )}
            {/* Hidden while the filter panel is open — the row is about
                narrowing results there, and Back owns the exit. */}
            {!showFilter && (
              <button
                className="tier-filter-toggle"
                onClick={toggleLibraryView}
                aria-label={libraryView === 'grid' ? 'Switch to list view' : 'Switch to grid view'}
                title={libraryView === 'grid' ? 'List view' : 'Grid view'}
              >
                {libraryView === 'grid' ? <Rows3 size={16} /> : <Grid2x2 size={16} />}
              </button>
            )}
            {!showFilter && (
              <button
                className="tier-filter-toggle"
                onClick={openAddTechnique}
                aria-label="Add a new technique"
                title="Add a new technique"
              >
                <Plus size={16} />
              </button>
            )}
            {/* Favorites only: jump to / back from the Archived view. Hidden when
                there's nothing archived (unless already viewing the archive). */}
            {activeTab === 'favorites' && (archivedView || techniques.some((t) => statusMap[t.id] === TechniqueStatus.Archived)) && (
              <button
                className={`tier-filter-toggle${archivedView ? ' tier-filter-toggle--active' : ''}`}
                onClick={() => { setArchivedView((v) => !v); setActiveCollectionId(null); }}
                aria-label={archivedView ? 'Back to favorites' : 'View archived techniques'}
                title={archivedView ? 'Back to Favorites' : 'Archived techniques'}
              >
                <Archive size={16} />
              </button>
            )}
          </div>
          {/* Toolbar wraps only the filter-pills row + panel — NOT the search
              row. The search row is a direct child of the scroll container so
              its sticky pin spans the whole list (a sticky element can't escape
              its containing block; nesting it here capped the pin to the short
              toolbar and released it once scrolled past). */}
          <div className="library-toolbar">
          {/* Filter bar below the search row: the toggle plus the active filter
              pills. Only shown when there ARE active filters — with none, the
              toggle lives in the search row instead of an empty pills row. Stays
              in normal flow and scrolls away under the pinned search row. */}
          {!showFilter && activeFilterCount > 0 && (
            <div className="active-filter-pills">
              <button
                className="tier-filter-toggle"
                onClick={toggleFilters}
                aria-label="Filter and view options"
                title="Filter and view options"
              >
                <Filter size={16} />
              </button>
              {activeFilters.map(({ key, label, tone, Icon, onRemove }) => (
                <button key={key} className={`active-filter-pill filter-tone--${tone}`} onClick={onRemove} aria-label={`Remove ${label} filter`}>
                  {Icon && <Icon size={13} />}
                  <span>{label}</span>
                  <X size={13} className="active-filter-pill-x" />
                </button>
              ))}
            </div>
          )}
          {showFilter && filterPanel()}
          </div>
          {!showFilter && (myMatOnly ? (
            (() => {
              // Favorites view: favorited techniques grouped by category. The
              // collection chips filter the list to one collection; dropping a
              // card on a chip adds it (handled in handleDragEnd).
              const activeCollection = activeCollectionId ? collections.find((c) => c.id === activeCollectionId) ?? null : null;
              const collSet = activeCollection ? new Set(activeCollection.techniqueIds) : null;
              const favCats = new Map<string, Technique[]>();
              for (const tier of TIERS) {
                const tierCats = tierData.get(tier);
                if (!tierCats) continue;
                for (const [catId, techs] of tierCats) {
                  const matched = techs.filter((t) => matchingTechIds.has(t.id) && (!collSet || collSet.has(t.id)));
                  if (matched.length === 0) continue;
                  favCats.set(catId, [...(favCats.get(catId) || []), ...matched]);
                }
              }
              const sorted = Array.from(favCats.entries()).sort(([a], [b]) =>
                (catMap.get(a)?.label || a).localeCompare(catMap.get(b)?.label || b));
              return (
                <>
                  {!archivedView && (
                    <CollectionChips
                      collections={collections}
                      activeId={activeCollectionId}
                      onSelect={setActiveCollectionId}
                      onCreate={createCollection}
                      onDelete={deleteCollection}
                      onOpenAll={openCollectionPips}
                    />
                  )}
                  {favCats.size === 0 ? (
                    <div className="empty-placeholder">
                      <div className="empty-placeholder-card">
                        {archivedView ? (
                          <>Tap the <Archive size={13} style={{ verticalAlign: '-2px' }} /> on a favorite to archive it here.</>
                        ) : activeCollection ? (
                          <>Drag a favorite onto “{activeCollection.name}” to add it to this collection.</>
                        ) : (
                          <>Tap the <Star size={13} style={{ verticalAlign: '-2px' }} /> on any technique to add it here.</>
                        )}
                      </div>
                    </div>
                  ) : (
                    <section className="tier-section tier-section--favorites">
                      <div className="categories-grid">
                        {sorted.map(([catId, techs]) => {
                          const cat = catMap.get(catId);
                          if (!cat) return null;
                          // Grouping runs on the already-filtered list, so a
                          // collection or search that leaves one version
                          // standing renders it as an ordinary card.
                          const groups = groupTechniques(techs);
                          const accordionKey = `fav-${catId}`;
                          const catOpen = catAccordion.isOpen(accordionKey);
                          return (
                            <CategoryCard
                              key={accordionKey}
                              label={cat.label}
                              open={catOpen}
                              onToggle={() => catAccordion.toggle(accordionKey)}
                              icon={CATEGORY_ICONS[cat.id]}
                              {...categoryRowProps(groups)}
                              onOpenAll={() => openPipGroup(techs, cat.label)}
                            >
                              {libraryView === 'grid' ? (
                                <div className="technique-grid">
                                  {groups.map((g) => { const tech = g.lead; return (
                                    <TechniqueTile
                                      key={g.key}
                                      id={tech.id}
                                      name={tech.name}
                                      status={statusMap[tech.id] || TechniqueStatus.Unchecked}
                                      categoryColor={TIER_ACCENT_COLORS[tech.tier as BeltRank]}
                                      instagramUrl={tech.instagramUrl}
                                      thumbnail={tech.thumbnail}
                                      watched={tech.instagramUrl ? watchedUrls.has(tech.instagramUrl) : false}
                                      onPipClick={(url, name) => handlePipClick(url, name, cat.label, tech.id)}
                                      onToggleMat={archivedView ? undefined : () => toggleMat(tech)}
                                      onInfoClick={() => setInfoTech(tech)}
                                      variantCount={g.variants.length}
                                      onVariantsClick={() => setVersionGroup(g)}
                                      onOpenAllVersions={() => openPipGroup(g.variants, g.name)}
                                    />
                                  ); })}
                                </div>
                              ) : groups.map((g) => { const tech = g.lead; return (
                                <TechniqueCard
                                  key={g.key}
                                  id={tech.id}
                                  name={tech.name}
                                  japaneseName={tech.japaneseName}
                            matchedAlias={aliasMatch(tech)}
                                  type={tech.type}
                                  status={statusMap[tech.id] || TechniqueStatus.Unchecked}
                                  categoryColor={TIER_ACCENT_COLORS[tech.tier as BeltRank]}
                                  instagramUrl={tech.instagramUrl}
                                  author={tech.author}
                                  attribution={tech.attribution}
                                  legality={legalityNote(tech, rules, arts)}
                                  rule={ruleFor(tech, rules)}
                                  watched={tech.instagramUrl ? watchedUrls.has(tech.instagramUrl) : false}
                                  onVideoClick={() => {}}
                                  onPipClick={(url, name) => handlePipClick(url, name, cat.label, tech.id)}
                                  onToggleMat={archivedView ? undefined : () => toggleMat(tech)}
                                  isAdmin={user?.uid === 'bZj8y9DApEac3eKOuKV08MKcaDx2'}
                                  onInfoClick={() => setInfoTech(tech)}
                                  onArchive={archivedView ? undefined : () => setStatus(tech.id, TechniqueStatus.Archived)}
                                  onUnarchive={archivedView ? () => setStatus(tech.id, TechniqueStatus.MarkedForPractice) : undefined}
                                  variantCount={g.variants.length}
                                  picking={versionGroup?.lead.id === g.lead.id}
                                  onVariantsClick={() => setVersionGroup(g)}
                                  onOpenAllVersions={() => openPipGroup(g.variants, g.name)}
                                />
                              ); })}
                            </CategoryCard>
                          );
                        })}
                      </div>
                    </section>
                  )}
                </>
              );
            })()
          ) : (<>
          {(() => {
            // Belt is now just a per-technique color + filter — group every
            // matching technique by category (merged across belts), in the
            // canonical category order. No more per-belt sections.
            const byCat = new Map<string, Technique[]>();
            for (const tier of TIERS) {
              const tierCats = tierData.get(tier);
              if (!tierCats) continue;
              for (const [catId, techs] of tierCats) {
                if (categoryFilter.size > 0 && !categoryFilter.has(catId)) continue;
                const matched = techs.filter((t) => matchingTechIds.has(t.id));
                if (matched.length) byCat.set(catId, [...(byCat.get(catId) || []), ...matched]);
              }
            }
            // Admin list modes (from Settings) swap the library to ONLY the
            // pending or archived items; regular techniques are hidden until
            // a bottom-nav tap resets the mode.
            const adminList = isAdminUser ? adminViewMode : null;
            const pendingForCat = (catId: string) => {
              if (adminList !== 'pending') return [] as Technique[];
              if (categoryFilter.size > 0 && !categoryFilter.has(catId)) return [];
              return pendingByCat.get(catId) || [];
            };
            const archivedForCat = (catId: string) => {
              if (adminList !== 'archived') return [] as Technique[];
              if (categoryFilter.size > 0 && !categoryFilter.has(catId)) return [];
              return archivedByCat.get(catId) || [];
            };
            return (
              <section className="tier-section">
                <div className="categories-grid">
                  {categories.filter((cat) => (adminList
                    ? pendingForCat(cat.id).length > 0 || archivedForCat(cat.id).length > 0
                    : byCat.has(cat.id))).map((cat) => {
                    const techs = adminList ? [] : byCat.get(cat.id) || [];
                    // Same move as Favorites: collapse only what survived the
                    // search / creator / belt / uniform filters, so a narrowed
                    // list never shows a chooser it doesn't need.
                    const groups = groupTechniques(techs);
                    const pendingItems = pendingForCat(cat.id);
                    const archivedItems = archivedForCat(cat.id);
                    const accordionKey = `cat-${cat.id}`;
                    const catOpen = catAccordion.isOpen(accordionKey);
                    return (
                      <CategoryCard
                        key={accordionKey}
                        label={cat.label}
                        open={catOpen}
                        onToggle={() => catAccordion.toggle(accordionKey)}
                        icon={CATEGORY_ICONS[cat.id]}
                        {...(adminList ? {} : categoryRowProps(groups))}
                        onOpenAll={() => openPipGroup(adminList ? [...pendingItems, ...archivedItems] : techs, cat.label)}
                      >
                        {pendingItems.map((tech) => (
                          <TechniqueCard
                            key={`pending-${tech.id}`}
                            id={tech.id}
                            name={tech.name}
                            japaneseName={tech.japaneseName}
                            matchedAlias={aliasMatch(tech)}
                            type={tech.type}
                            status={TechniqueStatus.Unchecked}
                            categoryColor={TIER_ACCENT_COLORS[tech.tier as BeltRank]}
                            instagramUrl={tech.instagramUrl}
                            author={tech.author}
                            attribution={tech.attribution}
                            legality={legalityNote(tech, rules, arts)}
                            rule={ruleFor(tech, rules)}
                            onVideoClick={() => {}}
                            onPipClick={(url, name) => handlePipClick(url, name, cat.label, tech.id)}
                            isAdmin
                            onInfoClick={() => setInfoTech(tech)}
                            reviewState="pending"
                            onApprove={() => approvePending(tech)}
                            onReject={() => rejectPending(tech)}
                          />
                        ))}
                        {libraryView === 'grid' ? (
                          <div className="technique-grid">
                            {groups.map((g) => { const tech = g.lead; return (
                              <TechniqueTile
                                key={g.key}
                                id={tech.id}
                                name={tech.name}
                                status={statusMap[tech.id] || TechniqueStatus.Unchecked}
                                categoryColor={TIER_ACCENT_COLORS[tech.tier as BeltRank]}
                                instagramUrl={tech.instagramUrl}
                                thumbnail={tech.thumbnail}
                                watched={tech.instagramUrl ? watchedUrls.has(tech.instagramUrl) : false}
                                onPipClick={(url, name) => handlePipClick(url, name, cat.label, tech.id)}
                                onToggleMat={() => toggleMat(tech)}
                                onInfoClick={() => setInfoTech(tech)}
                                variantCount={g.variants.length}
                                onVariantsClick={() => setVersionGroup(g)}
                                onOpenAllVersions={() => openPipGroup(g.variants, g.name)}
                              />
                            ); })}
                          </div>
                        ) : groups.map((g) => { const tech = g.lead; return (
                          <TechniqueCard
                            key={g.key}
                            id={tech.id}
                            name={tech.name}
                            japaneseName={tech.japaneseName}
                            matchedAlias={aliasMatch(tech)}
                            type={tech.type}
                            status={statusMap[tech.id] || TechniqueStatus.Unchecked}
                            categoryColor={TIER_ACCENT_COLORS[tech.tier as BeltRank]}
                            instagramUrl={tech.instagramUrl}
                            author={tech.author}
                            attribution={tech.attribution}
                            legality={legalityNote(tech, rules, arts)}
                            rule={ruleFor(tech, rules)}
                            watched={tech.instagramUrl ? watchedUrls.has(tech.instagramUrl) : false}
                            onVideoClick={() => {}}
                            onPipClick={(url, name) => handlePipClick(url, name, cat.label, tech.id)}
                            onToggleMat={() => toggleMat(tech)}
                            isAdmin={isAdminUser}
                            onInfoClick={() => setInfoTech(tech)}
                            variantCount={g.variants.length}
                            picking={versionGroup?.lead.id === g.lead.id}
                            onVariantsClick={() => setVersionGroup(g)}
                            onOpenAllVersions={() => openPipGroup(g.variants, g.name)}
                          />
                        ); })}
                        {archivedItems.map((tech) => (
                                  <TechniqueCard
                                    key={`archived-${tech.id}`}
                                    id={tech.id}
                                    name={tech.name}
                                    japaneseName={tech.japaneseName}
                            matchedAlias={aliasMatch(tech)}
                                    type={tech.type}
                                    status={TechniqueStatus.Unchecked}
                                    categoryColor={TIER_ACCENT_COLORS[tech.tier as BeltRank]}
                                    instagramUrl={tech.instagramUrl}
                                    author={tech.author}
                                    attribution={tech.attribution}
                                    legality={legalityNote(tech, rules, arts)}
                                    rule={ruleFor(tech, rules)}
                                    onVideoClick={() => {}}
                                    onPipClick={(url, name) => handlePipClick(url, name, cat.label, tech.id)}
                                    isAdmin
                                    onInfoClick={() => setInfoTech(tech)}
                                    reviewState="archived"
                                    onRestore={() => restoreArchived(tech)}
                                    onDelete={() => deleteArchived(tech)}
                                  />
                        ))}
                      </CategoryCard>
                    );
                  })}
                </div>
              </section>
            );
          })()}
          {isAdminUser && adminViewMode ? (
            (adminViewMode === 'pending' ? pendingByCat.size === 0 : archivedByCat.size === 0) && (
              <div className="no-results">
                {adminViewMode === 'pending' ? 'No pending submissions.' : 'No archived media.'}
              </div>
            )
          ) : visibleMatchCount === 0 && (
            // No art-specific branch: an art with no content reads the same as
            // any other empty result — the filters that emptied it are right
            // above, so the fix is in reach.
            searchQuery ? (
              <div className="no-results">No techniques found containing "{searchQuery}"</div>
            ) : activeFilterCount > 0 ? (
              <div className="no-results">Nothing matches these filters.</div>
            ) : (
              <div className="no-results">No techniques left to practice!</div>
            )
          )}
          </>)
          )}
        </div>
        )}

        {addModalCat && (
          <AddTechniqueModal
            categories={categories}
            existing={[...dbTechniques, ...customTechniques]}
            onAdd={handleAddTechnique}
            onCancel={() => setAddModalCat(null)}
          />
        )}

        {addedToast && (
          <div className="add-toast">"{addedToast}" added to your library</div>
        )}

        {toast && !addedToast && <div className="add-toast">{toast}</div>}

        {showArtPicker && (
          <ArtPicker current={arts} onPick={handlePickArts} onClose={() => setShowArtPicker(false)} />
        )}

        {(!preview || captureMode) && (
          <BottomNav
            active={activeTab}
            onChange={goTab}
            unreadChat={DEV_CHAT_ENABLED ? unreadChat : 0}
            onSettings={() => goTab('settings')}
          />
        )}

      </div>

      <DragOverlay dropAnimation={null} />
      {dragPos && activeTech && activeTier !== null && (
        <div className="drag-orb" style={{
          left: dragPos.x,
          top: dragPos.y,
        }} />
      )}
    </DndContext>
    {wallReason && (
      <PreviewWall
        reason={wallReason}
        // 'plays' leaves the last clip playing behind the wall, so there is
        // something to come back to. 'locked' was a tab that never opened.
        onDismiss={wallReason === 'plays' ? () => setWallReason(null) : () => { setWallReason(null); goTab('discover'); }}
      />
    )}
    {!preview && isAdminUser && (
      <button
        className="audit-fab"
        aria-label={exporting ? 'Exporting…' : 'Export library JSON'}
        title="Export library (JSON)"
        onClick={exportLibrary}
        disabled={exporting}
      >
        <Download size={56} strokeWidth={1.75} />
      </button>
    )}
    {soloVideo && (
      <PipWidget
        url={soloVideo.url}
        name={soloVideo.name}
        subtitle={soloVideo.subtitle}
        author={soloVideo.techniqueId ? authorById.get(soloVideo.techniqueId) : undefined}
        attribution={soloVideo.techniqueId ? attributionById.get(soloVideo.techniqueId) : undefined}
        legality={soloVideo.techniqueId ? legalityById.get(soloVideo.techniqueId) : undefined}
        onAuthor={openCreator}
        techniqueId={soloVideo.techniqueId}
        onClose={() => setSoloVideo(null)}
        onStar={soloVideo.techniqueId ? () => toggleMatById(soloVideo.techniqueId!) : undefined}
        starred={soloVideo.techniqueId ? statusMap[soloVideo.techniqueId] === TechniqueStatus.MarkedForPractice : false}
        muted={preview}
      />
    )}

    {infoTech && (() => {
      const isPendingItem = pendingSubs.some((p) => p.id === infoTech.id);
      const isArchivedItem = archivedTechs.some((a) => a.id === infoTech.id);
      // Added on this device and stored in localStorage, not in the library —
      // its owner edits it, and neither updateTechnique nor archiveTechnique
      // has a document to work on.
      const isLocalItem = customTechniques.some((t) => t.id === infoTech.id);
      // Archive from the info modal only makes sense for approved techniques —
      // pending items are approved/rejected from the row, and already-archived
      // items are restored/deleted from the row.
      const canArchiveHere = canEditLibrary && !isPendingItem && !isArchivedItem && !isLocalItem;
      return (
        <TechniqueInfoModal
          key={infoTech.id}
          tech={infoTech}
          categories={categories}
          existing={[...dbTechniques, ...customTechniques]}
          canEdit={canEditLibrary || isLocalItem}
          onClose={() => setInfoTech(null)}
          onSaved={(patch) => setInfoTech((cur) => (cur ? { ...cur, ...patch } : cur))}
          onSaveLocal={isLocalItem ? (patch) => setCustomTechniques((prev) => {
            const next = prev.map((t) => (t.id === infoTech.id ? { ...t, ...patch } : t));
            saveCustomTechniques(next);
            return next;
          }) : undefined}
          onArchive={canArchiveHere ? () => archiveTechnique(infoTech) : undefined}
        />
      );
    })()}

    {/* Version chooser for a grouped card — the same technique filmed more
        than once. Every action on it is per-clip, and picking one closes the
        sheet straight into the player. */}
    {versionGroup && (
      <VersionPicker
        group={versionGroup}
        statusOf={(id) => statusMap[id] || TechniqueStatus.Unchecked}
        isWatched={(t) => (t.instagramUrl ? watchedUrls.has(t.instagramUrl) : false)}
        onPlay={(t) => { setVersionGroup(null); playTechnique(t); }}
        onToggleMat={archivedView ? undefined : (t) => toggleMat(t)}
        onInfo={(t) => { setVersionGroup(null); setInfoTech(t); }}
        onClose={() => setVersionGroup(null)}
      />
    )}

    {/* Floating PiPs — single-clip windows, draggable & resizable. */}
    {!preview && pipWindows.filter((w) => !w.groupId).map((w, i) => (
      <PipWidget
        key={w.id}
        url={w.url}
        name={w.name}
        subtitle={w.subtitle}
        author={w.techniqueId ? authorById.get(w.techniqueId) : undefined}
        attribution={w.techniqueId ? attributionById.get(w.techniqueId) : undefined}
        legality={w.techniqueId ? legalityById.get(w.techniqueId) : undefined}
        onAuthor={openCreator}
        techniqueId={w.techniqueId}
        dormant={w.dormant}
        bounceNonce={pipBounce.id === w.id ? pipBounce.n : 0}
        onWake={() => {
          setPipWindows((prev) => {
            const next = prev.map((p) => p.id === w.id ? { ...p, dormant: false } : p);
            savePipWindows(next);
            return next;
          });
        }}
        onClose={() => closePipWindow(w.id)}
        onStar={w.techniqueId ? () => toggleMatById(w.techniqueId!) : undefined}
        starred={w.techniqueId ? statusMap[w.techniqueId] === TechniqueStatus.MarkedForPractice : false}
        onMove={(x, y) => updatePipPosition(w.id, x, y)}
        onResize={(width, height) => updatePipSize(w.id, width, height)}
        style={{
          ...(w.x != null && w.y != null
            ? { top: w.y, left: w.x, bottom: 'auto', right: 'auto' }
            : { bottom: 16 + i * 20, right: 16 + i * 20 }),
          ...(w.w ? { width: w.w } : {}),
        }}
      />
    ))}

    {/* Group grids — every video in a collection/category in one responsive flow
        container that wraps and shrinks with the viewport (desktop only). The bar
        on top holds the title, paging, and close-all. */}
    {!preview && Array.from(new Set(pipWindows.map((w) => w.groupId).filter(Boolean))).map((gid) => {
      const members = pipWindows.filter((w) => w.groupId === gid);
      const group = gid ? pipGroups[gid] : undefined;
      const pages = group ? Math.ceil(group.items.length / PIP_PAGE_SIZE) : 1;
      const offset = (gid && pipGroupOffsets[gid]) || { x: 0, y: 0, scale: 1 };
      return (
        <div
          className="pip-group-grid"
          key={`pip-group-${gid}`}
          style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${offset.scale})` }}
        >
          <PipGroupBar
            title={members[0]?.groupTitle}
            page={group?.page ?? 0}
            pages={pages}
            onPrev={() => pagePipGroup(gid!, -1)}
            onNext={() => pagePipGroup(gid!, 1)}
            onClose={() => closePipGroup(gid!)}
            offset={offset}
            onOffsetChange={(o) => setPipGroupOffsets((prev) => ({ ...prev, [gid!]: o }))}
          />
          <div className="pip-group-tiles" style={{ gridTemplateColumns: `repeat(${pipGridCols(members.length)}, auto)` }}>
            {members.map((w) => (
              <PipWidget
                key={w.id}
                url={w.url}
                name={w.name}
                subtitle={w.subtitle}
                author={w.techniqueId ? authorById.get(w.techniqueId) : undefined}
                attribution={w.techniqueId ? attributionById.get(w.techniqueId) : undefined}
                legality={w.techniqueId ? legalityById.get(w.techniqueId) : undefined}
                onAuthor={openCreator}
                techniqueId={w.techniqueId}
                dormant={w.dormant}
                tiled
                bounceNonce={pipBounce.id === w.id ? pipBounce.n : 0}
                onWake={() => {
                  setPipWindows((prev) => {
                    const next = prev.map((p) => p.id === w.id ? { ...p, dormant: false } : p);
                    savePipWindows(next);
                    return next;
                  });
                }}
                onClose={() => closePipWindow(w.id)}
                onStar={w.techniqueId ? () => toggleMatById(w.techniqueId!) : undefined}
                starred={w.techniqueId ? statusMap[w.techniqueId] === TechniqueStatus.MarkedForPractice : false}
              />
            ))}
          </div>
        </div>
      );
    })}
    </>
  );
}
